import { useState, useEffect, useRef } from "react";
import { formatBRDate, formatBRDateTimeShort } from "@/lib/datetime";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NOME_DO_TURNO, TURNOS, comLinhaDoTroco, horarioDosTurnos, entregaPorBairroSemSaida } from "@workspace/pricing";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import {
  Loader2,
  Save,
  Store as StoreIcon,
  Palette,
  CreditCard,
  Truck,
  Clock,
  Link as LinkIcon,
  Crown,
  Image as ImageIcon,
  Copy,
  Check,
  ExternalLink,
  LogOut,
  X,
  Bell,
  BellOff,
  MessageCircle as MessageCircleIcon,
  Package,
  Sparkles,
  RefreshCw,
  ChevronDown,
  Plus,
  Trash2,
  MapPin,
  Printer,
  AlertTriangle,
  Download,
  Volume2,
  Users,
  Rocket,
  PauseCircle,
} from "lucide-react";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { AvisoDoIphone, tipoDesteIphone } from "@/components/AvisoDoIphone";
import { getOrderSoundMode, setOrderSoundMode, previewOrderSound, type OrderSoundMode } from "@/lib/order-sound";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TimeField, hmToMinutes, minutesToHM } from "@/components/ui/time-field";
import { EDITED_ORDER_MESSAGE_DEFAULT } from "@/lib/messages";
import { useThermalPrinter, isWebUsbSupported } from "@/hooks/use-thermal-printer";
import { InstallAppButton } from "@/components/InstallAppButton";
import { EVENTO_DA_SECAO, SettingsSectionNav } from "@/components/admin/SettingsSectionNav";
import { ESC_POS, text, concat, line, separator, buildReceipt } from "@/lib/thermal-printer";
import { AddressAutocomplete, type AddressData } from "@/components/AddressAutocomplete";
import { CurrencyInput } from "@/components/ui/currency-input";
import { NumberInput } from "@/components/ui/number-input";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { useToast } from "@/hooks/use-toast";
import { customFetch, useListProducts } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";
import { trackEvent } from "@/hooks/use-tracking";
import { getPublicHost, formatWhatsApp, formatCurrency } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { THEMES, CUSTOM_THEME_NAME, findClosestTheme, type Theme } from "@/lib/themes";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { useSectionAutoSave } from "@/hooks/use-section-auto-save";
import {
  PlanPicker,
  PLAN_DEFS,
  priceCents,
  totalCents,
  formatBRL,
  type PaidPlanId,
  type BillingCycleUI,
  isPaidPlanId,
  isUltra,
} from "@/components/PlanPicker";
import { UltraBadge } from "@/components/UltraBadge";
import { useFeatureAllowed, useIsPaidPlan } from "@/hooks/use-plan";
import { erroDoRodape, normalizarRodape, rodapeParaGravar, RODAPE_MAX_CARACTERES, RODAPE_PADRAO } from "@workspace/escpos";
import { LoyaltySection } from "@/components/admin/LoyaltySection";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";
import { PrintAgentSection, usePrintAgentDevices, deviceRecente } from "@/components/PrintAgentSection";
import { CouponsSection } from "@/components/admin/CouponsSection";
import { EquipeSection } from "@/components/admin/EquipeSection";
import { WhatsAppBotSection } from "@/components/admin/WhatsAppBotSection";
import { TableOrdersSection } from "@/components/admin/TableOrdersSection";
import { WhatsappVerifyModal } from "@/components/WhatsappVerifyModal";

type DistanceRange = { minKm: number; maxKm: number | null; fee: number; noDelivery: boolean };

// Normaliza as faixas de distância pro modelo novo:
// - REMOVE faixas legadas de "não entrega" (noDelivery=true). No padrão antigo,
//   "não entrega acima de X" era uma faixa [X, ∞) com noDelivery. No modelo novo
//   isso é implícito (acima da última faixa finita = não entrega, tratado no
//   backend), então essas faixas saem — senão, sem o checkbox, virariam entrega
//   grátis. (Os dados em prod foram migrados; isto blinda edições/dados antigos.)
// - Encadeia minKm: a 1ª começa em 0, as demais onde a anterior terminou. Evita
//   gap entre faixas (ex.: 0–2 e 5–7 deixando 2–5 sem cobrança).
function chainDistanceRanges(ranges: DistanceRange[]): DistanceRange[] {
  return ranges
    .filter((r) => !r.noDelivery)
    .map((r, i, arr) => ({
      ...r,
      minKm: i === 0 ? 0 : (arr[i - 1]?.maxKm ?? 0),
      noDelivery: false,
    }));
}

interface ChargeHistoryResp {
  payments: Array<{
    id: string;
    billingType: string;
    value: string;
    status: string;
    dueDate: string | null;
    confirmedDate: string | null;
    paidAt: string | null;
    createdAt: string;
  }>;
  subscription: {
    status: string;
    billingType: string;
    value: string;
    nextDueDate: string | null;
    cancelScheduledAt: string | null;
    retryCount: number;
    nextRetryDate: string | null;
  } | null;
}

// Mapeia o status Asaas da cobrança pra rótulo + cor PT-BR. RECEIVED/CONFIRMED
// = pago; OVERDUE = venceu sem pagar (falha do débito PIX / cartão recusado);
// PENDING = QR/cobrança gerada aguardando pagamento.
function chargeStatusPill(status: string): { label: string; className: string } {
  const s = (status || "").toUpperCase();
  if (s === "RECEIVED" || s === "CONFIRMED" || s === "RECEIVED_IN_CASH") {
    return { label: "Pago", className: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" };
  }
  if (s === "OVERDUE") {
    return { label: "Não paga", className: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" };
  }
  if (s === "PENDING" || s.startsWith("AWAITING")) {
    return { label: "Aguardando", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" };
  }
  if (s.startsWith("REFUND")) {
    return { label: "Estornado", className: "bg-muted text-muted-foreground" };
  }
  return { label: status || "—", className: "bg-muted text-muted-foreground" };
}

// Data exibida pro lojista: quando o dinheiro SAI/SAIU/VENCERIA na conta dele —
// nunca o created_at, que no PIX automático é a CRIAÇÃO da instrução (3 dias
// antes do vencimento, exigência da Asaas) e confundia ("por que 19/07 se a
// cobrança é dia 22?"). Datas date-only formatadas por slice pra não passar por
// Date() (meia-noite UTC viraria o dia anterior em BRT).
function chargeDateLabel(p: ChargeHistoryResp["payments"][number]): string {
  const s = (p.status || "").toUpperCase();
  const dmy = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  const paid = s === "RECEIVED" || s === "CONFIRMED" || s === "RECEIVED_IN_CASH";
  if (paid && (p.paidAt || p.confirmedDate)) {
    // paid_at é o carimbo do webhook (18/09/2026) e vale pros dois caminhos:
    // QR pago na hora e débito automático, que antes ficava sem horário porque
    // a linha nascia em D-3 e confirmed_date é date-only.
    if (p.paidAt) return `Pago em ${formatBRDateTimeShort(p.paidAt)}`;
    return `Pago em ${dmy(p.confirmedDate!)}`;
  }
  if (s === "OVERDUE" && p.dueDate) return `Venceu em ${dmy(p.dueDate)}`;
  if ((s === "PENDING" || s.startsWith("AWAITING")) && p.dueDate) {
    return `Cobrança em ${dmy(p.dueDate)}`;
  }
  return formatBRDateTimeShort(p.createdAt);
}

function SubscriptionCharges({ standalone = false }: { standalone?: boolean }) {
  const { data, isLoading, isError } = useQuery<ChargeHistoryResp>({
    queryKey: ["asaas-payment-history"],
    queryFn: () => customFetch("/api/asaas/payment-history"),
  });

  const sub = data?.subscription ?? null;
  // Aviso de falha SÓ quando existe cobrança REAL vencida sem pagamento —
  // mesma regra da régua de WhatsApp (nada de alarmar antes do dia do débito).
  // retry_count > 0 sozinho vaza falha INTERNA de criação da instrução (D-3,
  // regra dos 3 dias da Asaas) com o card acima ainda dizendo "Ativo" (caso
  // Bem Bolado, 24/07: última cobrança PAGA e o painel dizia "falhou").
  const newest = data?.payments?.[0] ?? null;
  const todayBrt = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  const newestUnpaidDue =
    !!newest &&
    !["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes((newest.status || "").toUpperCase()) &&
    !!newest.dueDate &&
    newest.dueDate <= todayBrt;
  const inRetry = !!sub && (sub.retryCount ?? 0) > 0 && sub.status === "ACTIVE" && newestUnpaidDue;

  // No grátis isto vira seção própria: o card "Gerenciar Assinatura" que
  // embrulha o histórico no plano pago não existe lá. Só aparece pra quem já
  // pagou algum dia — loja que nunca assinou não ganha um card vazio.
  if (standalone && (isLoading || isError || !data?.payments?.length)) return null;

  const inner = (
    <>
      <p className="text-sm font-semibold mb-2">Histórico de cobranças</p>

      {inRetry && (
        <div className="mb-2 text-xs rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300">
          A última cobrança falhou —{" "}
          {sub!.nextRetryDate
            ? <>nova tentativa em {formatBRDate(sub!.nextRetryDate)}.</>
            : <>gere um novo PIX ou atualize o cartão para regularizar.</>}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground py-2">Não foi possível carregar o histórico agora.</p>
      ) : !data?.payments?.length ? (
        <p className="text-sm text-muted-foreground py-2">Nenhuma cobrança registrada ainda.</p>
      ) : (
        <div className="rounded-lg border border-border/50 divide-y divide-border/40">
          {data.payments.map((p) => {
            const pill = chargeStatusPill(p.status);
            return (
              <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium">{formatBRL(Math.round(Number(p.value) * 100))}</p>
                  <p className="text-xs text-muted-foreground">
                    {chargeDateLabel(p)} · {p.billingType === "CREDIT_CARD" ? "Cartão" : "PIX"}
                  </p>
                </div>
                <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${pill.className}`}>
                  {pill.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return standalone ? (
    <Card className="p-6 mt-4">{inner}</Card>
  ) : (
    <div className="mt-3 pt-3 border-t border-border/40">{inner}</div>
  );
}

// Campos de que o `address` da loja é montado ao salvar a seção de entrega.
const ENDERECO_DA_LOJA = ["storeStreet", "storeNumber", "storeComplement", "storeNeighborhood", "storeCity", "storeState"];

const digitosDoNumero = (v: unknown) => String(v ?? "").replace(/\D/g, "").length;

const AUTO_SAVE_SECTIONS: Record<string, string[]> = {
  // O «slug» (Link da Loja) não entra: salva só ao sair do campo (salvarLink).
  general: [
    "name",
    "logoUrl",
    "storeType",
    "isActive",
    "address",
    "whatsappNumber",
    "whatsappDdi",
    "loginWhatsappNumber",
    "loginWhatsappDdi",
    "suggestiveText",
    "promotionBadgeText",
  ],
  delivery: [
    "allowsDelivery",
    "allowsPickup",
    "pickupMethods",
    "deliveryFee",
    "deliveryFeeType",
    "deliveryDistanceRanges",
    "distanceBeyondMode",
    "deliveryNeighborhoods",
    "acceptUnlistedNeighborhoods",
    "unlistedNeighborhoodFee",
    "unlistedNeighborhoodMode",
    "manualAddressMode",
    "manualAddressFields",
    "locationLinkInOrder",
    "deliveryFeeExpenseEnabled",
    "freeDeliveryAbove",
    "freeDeliveryBeforeCoupon",
    "couponCoversDelivery",
    "storeZipCode",
    "storeStreet",
    "storeNumber",
    "storeComplement",
    "storeNeighborhood",
    "storeCity",
    "storeState",
    "storeLat",
    "storeLng",
  ],
  payments: ["acceptedPayments", "acceptedPaymentsByMode", "cardSurchargeConfig", "cardFeeExpenseConfig", "allowMixedPayments", "cashRegisterEnabled", "crediarioMethods"],
  hours: ["businessHours", "timezone", "acceptOrdersWhenClosed"],
  order: ["minOrderValue", "orderMessageTemplate"],
  templates: [
    "appointmentMessageTemplate",
    "appointmentRequestTemplate",
    "statusMessageTemplates",
  ],
  pdv: ["pdvSettings"],
  products: ["hidePausedProducts", "hideOutOfStockProducts", "hideUnavailableOptions", "dailyOrderNumbering", "orderCodeStyle", "dailyOrderStart", "showGroupTitles", "orderMessageShowDate", "showOptionStock", "showPublicCoupons", "stackableCoupons", "showBestSellers", "featuredProducts", "costExpenseEnabled", "orderDayLimit", "orderSlotExclusive", "orderSlotIntervalMinutes", "orderSingleDate", "orderShiftHours"],
  clientes: ["askBirthDate", "birthDateRequired", "birthdayReminders", "birthdayAdvanceDays", "birthdayPastDays", "birthdayPrintEnabled", "birthdayWhatsappMessage", "askGiftRecipient", "giftRecipientRequired"],
  loyalty: ["loyaltyEnabled", "loyaltyConfig"],
  mesa: ["tableOrdersEnabled", "tableCount", "tableQrPerClient", "tablePrintFullBill", "tablePrintBillRequest", "tableQrOnly", "tablePrintOnClose", "tableShowClosed"],
  impressora: ["printCopies", "receiptTopMargin", "receiptPaidCheckbox", "receiptFont", "receiptFooterMessage"],
};

// Fusos do Brasil com nome amigável, no topo do seletor. O resto do mundo vem
// de Intl.supportedValuesOf (com fallback silencioso em navegador antigo).
const BRAZIL_TIMEZONES: Array<{ id: string; label: string }> = [
  { id: "America/Sao_Paulo", label: "Brasília / São Paulo (GMT-3)" },
  { id: "America/Fortaleza", label: "Fortaleza / Ceará (GMT-3)" },
  { id: "America/Recife", label: "Recife / Pernambuco (GMT-3)" },
  { id: "America/Bahia", label: "Salvador / Bahia (GMT-3)" },
  { id: "America/Belem", label: "Belém / Pará (GMT-3)" },
  { id: "America/Manaus", label: "Manaus / Amazonas (GMT-4)" },
  { id: "America/Cuiaba", label: "Cuiabá / Mato Grosso (GMT-4)" },
  { id: "America/Campo_Grande", label: "Campo Grande / MS (GMT-4)" },
  { id: "America/Boa_Vista", label: "Boa Vista / Roraima (GMT-4)" },
  { id: "America/Porto_Velho", label: "Porto Velho / Rondônia (GMT-4)" },
  { id: "America/Rio_Branco", label: "Rio Branco / Acre (GMT-5)" },
  { id: "America/Noronha", label: "Fernando de Noronha (GMT-2)" },
];

const WORLD_TIMEZONES: Array<{ id: string; label: string }> = (() => {
  let ids: string[] = [];
  try {
    ids = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    ids = [];
  }
  const brazil = new Set(BRAZIL_TIMEZONES.map((t) => t.id));
  return ids
    .filter((id) => !brazil.has(id))
    .map((id) => {
      let offset = "";
      try {
        offset =
          new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "shortOffset" })
            .formatToParts(new Date())
            .find((p) => p.type === "timeZoneName")?.value ?? "";
      } catch { /* fuso não suportado pelo runtime — sem offset no label */ }
      return { id, label: `${id.replace(/_/g, " ")}${offset ? ` (${offset})` : ""}` };
    });
})();

function generateSlug(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/(^-|-$)/g, "");
}

// Campo «Link da Loja»: link colado (o do próprio cardápio, Instagram, wa.me…) virava uma tripa de 200+ letras
// («httpsvendanozapapploja…»). Agora fica o último pedaço do caminho, sem ?… e #…, e o campo para em 40 letras.
// `cortou`: o campo deixou algo de fora (do link ou do limite), para avisar embaixo.
const SLUG_MAX = 40;

// A limpeza do generateSlug (e do cleanSlug do servidor): o acento sai e a letra fica, espaço vira «-» e «--» vira um
// só. O tracinho do fim só sai ao sair do campo (handleSlugBlur): digitando, «acai-» tem de ficar para vir o «no».
const limparLink = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "").replace(/-{2,}/g, "-").replace(/^-+/, "");

function slugDoCampo(valor: string): { slug: string; cortou: boolean } {
  let base = valor;
  if (/:\/\/|www\.|[/?#]/i.test(valor)) {
    const link = valor.match(/(?:https?:\/\/|www\.)\S+/i)?.[0] ?? valor.trim();
    const pedacos = link.split(/[?#]/)[0].split("/").filter(Boolean);
    // Link sem caminho: o último pedaço é o próprio domínio, e o «www.» não é dele.
    base = (pedacos[pedacos.length - 1] ?? "").replace(/^www\./i, "");
  }
  const slug = limparLink(base).slice(0, SLUG_MAX);
  return { slug, cortou: slug.length < limparLink(valor).length };
}

interface StatusMessageTemplate {
  template: string;
  enabled: boolean;
}

const ALL_ORDER_STATUSES_FOR_PRINT = [
  { id: 'novo', label: 'Pendente' },
  { id: 'confirmado', label: 'Confirmado' },
  { id: 'em_preparo', label: 'Em Preparo' },
  { id: 'em_separacao', label: 'Em Separação' },
  { id: 'pronto', label: 'Pronto' },
  { id: 'aguardando_retirada', label: 'Disp. p/ Retirada' },
  { id: 'avisou_motoboy', label: 'Avisou Motoboy' },
  { id: 'enviado', label: 'Saiu p/ Entrega' },
  { id: 'entregue', label: 'Entregue' },
  { id: 'cancelado', label: 'Cancelado' },
];

type ManualAddressFieldKey =
  | "cep"
  | "state"
  | "city"
  | "neighborhood"
  | "street"
  | "number"
  | "complement"
  | "reference";

const MANUAL_ADDRESS_FIELD_ORDER: ManualAddressFieldKey[] = [
  "cep",
  "state",
  "city",
  "neighborhood",
  "street",
  "number",
  "complement",
  "reference",
];

const MANUAL_ADDRESS_FIELD_LABELS: Record<ManualAddressFieldKey, string> = {
  cep: "CEP",
  state: "Estado",
  city: "Cidade",
  neighborhood: "Bairro",
  street: "Rua",
  number: "Número",
  complement: "Complemento",
  reference: "Ponto de referência",
};

const MANUAL_ADDRESS_FIELD_DEFAULTS: Record<ManualAddressFieldKey, { enabled: boolean; required: boolean }> = {
  cep:          { enabled: false, required: false },
  state:        { enabled: false, required: false },
  city:         { enabled: false, required: false },
  neighborhood: { enabled: true,  required: true },
  street:       { enabled: true,  required: false },
  number:       { enabled: true,  required: false },
  complement:   { enabled: true,  required: false },
  reference:    { enabled: false, required: false },
};

// Loja que nunca abriu esta seção não tem nada salvo — e o link público, nesse
// caso, mostra CEP/cidade/estado (é o formulário histórico da busca do Google).
// Os DEFAULTS acima descrevem o formulário manual enxuto; exibi-los aqui fazia a
// tela dizer "CEP desligado" enquanto o cliente via o CEP. Pior: ao ligar
// qualquer chave, o conjunto inteiro era gravado e o CEP sumia do checkout sem
// ninguém ter pedido. Este é o estado REAL de quem não configurou.
const MANUAL_ADDRESS_FIELDS_EFFECTIVE: Record<ManualAddressFieldKey, { enabled: boolean; required: boolean }> = {
  ...MANUAL_ADDRESS_FIELD_DEFAULTS,
  cep:   { enabled: true, required: false },
  state: { enabled: true, required: false },
  city:  { enabled: true, required: false },
};

interface StoreSettings {
  id: string;
  name: string;
  slug: string;
  whatsappNumber: string;
  // WhatsApp de LOGIN da conta (users), separado do de pedidos desde 09/09/2026.
  loginWhatsappNumber?: string | null;
  logoUrl: string | null;
  colorPrimary: string;
  colorSecondary: string;
  colorAccent: string;
  themeName: string | null;
  orderMessageTemplate: string | null;
  appointmentMessageTemplate: string | null;
  appointmentRequestTemplate: string | null;
  acceptedPayments: string[] | null;
  acceptedPaymentsByMode: { delivery: string[]; pickup: string[] } | null;
  cardSurchargeConfig: Record<string, { enabled: boolean; type: "percent" | "fixed"; value: number; includeDelivery: boolean }> | null;
  // Taxa da maquininha lançada como despesa (Débito e Crédito; Profissional). Campo à parte do acréscimo: a aba velha que
  // salva o acréscimo não apaga a taxa, e o contrário também.
  cardFeeExpenseConfig?: Record<string, { enabled: boolean; percent: number }> | null;
  allowsDelivery: boolean;
  allowsPickup: boolean;
  pickupMethods?: string[] | null;
  deliveryFee: string | null;
  deliveryFeeType: string;
  storeZipCode: string | null;
  storeStreet: string | null;
  storeNumber: string | null;
  storeComplement: string | null;
  storeNeighborhood: string | null;
  storeCity: string | null;
  storeState: string | null;
  storeLat: number | null;
  storeLng: number | null;
  deliveryDistanceRanges: Array<{ minKm: number; maxKm: number | null; fee: number; noDelivery: boolean }> | null;
  // Acima da última faixa: "no_delivery" (não entrega) ou "on_request" (frete calculado após o pedido).
  distanceBeyondMode: string;
  deliveryNeighborhoods: Array<{ name: string; fee: number; minOrder?: number }> | null;
  acceptUnlistedNeighborhoods: boolean;
  unlistedNeighborhoodFee: string | null;
  unlistedNeighborhoodMode: string;
  manualAddressMode: boolean;
  manualAddressFields: Record<string, { enabled: boolean; required: boolean }> | null;
  // «Receber o link da localização no pedido»: desligada, a mensagem do pedido sai sem a linha «Localização».
  locationLinkInOrder?: boolean;
  // «Lançar a taxa de entrega como despesa» (Profissional): no Entregue, a taxa vira despesa no Financeiro.
  deliveryFeeExpenseEnabled?: boolean;
  freeDeliveryAbove: string | null;
  // Cupom × entrega (22/09/2026): ver couponMath em @workspace/pricing.
  freeDeliveryBeforeCoupon?: boolean;
  couponCoversDelivery?: boolean;
  minOrderValue: string | null;
  isActive: boolean;
  businessHours: Record<string, { enabled: boolean; ranges: Array<{ start: string; end: string }> }> | null;
  storeType: string;
  hidePausedProducts: boolean;
  // «Esconder produtos esgotados do link público» (Pedro, 24/10/2026, consulta #642): estoque zerado some do link.
  hideOutOfStockProducts?: boolean;
  hideUnavailableOptions?: boolean;
  dailyOrderNumbering: boolean;
  orderCodeStyle?: string;
  dailyOrderStart?: number | null;
  orderSlotExclusive?: boolean | null;
  orderSlotIntervalMinutes?: number | null;
  // Uma encomenda por dia (15/10/2026): o servidor ainda manda, sincronizado com o limite (true = há limite).
  orderDayExclusive?: boolean | null;
  // Até N pedidos por dia (18/10/2026): null = sem limite. Com N = 1 a "uma por horário" fica sem efeito.
  orderDayLimit?: number | null;
  orderSingleDate?: boolean;
  // Horário dos turnos da encomenda por turno (12/10/2026); null = padrão (8h–12h, 12h–18h, 18h+).
  orderShiftHours?: Record<string, { start: string; end: string | null; ligado?: boolean } | null> | null;
  // Tem produto por encomenda em Turno? Só então «Horário dos turnos» aparece (o que está salvo fica guardado).
  hasShiftProducts?: boolean;
  showGroupTitles: boolean;
  orderMessageShowDate?: boolean;
  showOptionStock?: boolean;
  showPublicCoupons?: boolean;
  stackableCoupons?: boolean;
  showBestSellers?: boolean;
  featuredProducts?: string[] | null;
  printCopies: number;
  receiptTopMargin?: number;
  receiptPaidCheckbox?: boolean;
  // «Letra maior no cupom»: campo próprio da API (o servidor grava no pdvSettings.receiptFont).
  receiptFont?: "alta" | "normal";
  // «Mensagem no rodapé do cupom» (Profissional): o texto guardado. O que sai no cupom vem em receiptFooterPrinted.
  receiptFooterMessage?: string | null;
  timezone: string;
  acceptOrdersWhenClosed: boolean;
  allowMixedPayments: boolean;
  cashRegisterEnabled: boolean;
  costExpenseEnabled?: boolean;
  crediarioMethods?: Array<{ name: string; allowPublic?: boolean; installments?: number; interestMode?: "total" | "parcela"; interestPercent?: number; interestPerInstallment?: number }>;
  askBirthDate: boolean;
  birthdayReminders?: boolean | null;
  birthdayAdvanceDays?: number | null;
  birthdayPastDays?: number | null;
  birthdayPrintEnabled?: boolean | null;
  birthdayWhatsappMessage?: string | null;
  birthDateRequired: boolean;
  askGiftRecipient?: boolean;
  giftRecipientRequired?: boolean;
  loyaltyEnabled?: boolean;
  loyaltyConfig?: unknown;
  tableOrdersEnabled?: boolean;
  tableCount?: number | null;
  tableQrPerClient?: boolean;
  tablePrintFullBill?: boolean;
  tablePrintBillRequest?: boolean;
  tableQrOnly?: boolean;
  tablePrintOnClose?: boolean;
  tableShowClosed?: boolean;
  address: string | null;
  plan: string;
  planBillingCycle?: string;
  monthlyOrderCount: number;
  orgMonthlyOrderCount?: number | null;
  orgUnitCount?: number;
  statusMessageTemplates: Record<string, StatusMessageTemplate> | null;
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionCancelAt?: string | null;
  subscriptionCurrentPeriodEnd?: string | null;
  subscriptionAutoRenewBroken?: boolean;
}

function useSettings() {
  return useQuery<StoreSettings>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
  });
}

const paymentMethods = [
  { key: "Dinheiro", label: "Dinheiro" },
  { key: "Pix", label: "PIX" },
  { key: "Débito", label: "Cartão de Débito" },
  { key: "Crédito", label: "Cartão de Crédito" },
  { key: "Vale Refeição", label: "Vale Refeição" },
  { key: "Vale Alimentação", label: "Vale Alimentação" },
];

const SURCHARGE_METHODS = [
  { key: "Débito", label: "Cartão de Débito" },
  { key: "Crédito", label: "Cartão de Crédito" },
  { key: "Vale Refeição", label: "Vale Refeição" },
  { key: "Vale Alimentação", label: "Vale Alimentação" },
] as const;

const BH_DAYS = [
  { key: "1", label: "Segunda" },
  { key: "2", label: "Terça" },
  { key: "3", label: "Quarta" },
  { key: "4", label: "Quinta" },
  { key: "5", label: "Sexta" },
  { key: "6", label: "Sábado" },
  { key: "0", label: "Domingo" },
];

const bhTimeOptions: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    bhTimeOptions.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}
bhTimeOptions.push("23:59");

// Passo da lista — também o empurrão quando início/fim se cruzam.
const BH_STEP = 30;

// O fim tem que ser depois do início, até 23:59 (Pedro, 23/10/2026). O servidor lê a faixa no mesmo dia: «17:00 às
// 00:00» ou «18:00 às 02:00» deixavam a loja fechada a faixa inteira, e a API recusa a faixa nova ou mudada assim.
// null = faixa boa.
const MSG_MEIA_NOITE = "Passa da meia-noite? Termine às 23:59 e coloque o resto no dia seguinte, a partir de 00:00.";
function problemaDaFaixa(start: string, end: string): string | null {
  const i = hmToMinutes(start);
  const f = hmToMinutes(end);
  if (i == null || f == null) return null;
  // Fim até 06:00 antes do início é a noite que vira; depois das 06:00, é início e fim trocados (18:00–12:00): para essa
  // loja o «Terminar às 23:59» erraria (ela queria 12:00–18:00). O mesmo corte da divisão dos dados (23/10).
  if (f === 0 || (f < i && f <= 6 * 60)) return MSG_MEIA_NOITE;
  if (f <= i) return "O fim tem que ser depois do início.";
  return null;
}

function normalizeBH(raw: any): { enabled: boolean; ranges: Array<{ start: string; end: string }> } {
  if (!raw) return { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] };
  if (raw.ranges && Array.isArray(raw.ranges)) return raw;
  if (raw.open !== undefined && raw.close !== undefined) {
    return { enabled: !!raw.enabled, ranges: [{ start: raw.open, end: raw.close }] };
  }
  return { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] };
}

const STATUS_MESSAGE_DEFAULTS: Record<string, { label: string; template: string }> = {
  confirmado: {
    label: "Confirmado",
    template: "Olá {cliente_nome}! Seu pedido #{numero_pedido} foi confirmado! Iremos preparar tudo para você. 😊",
  },
  em_preparo: {
    label: "Em Preparo",
    template: "{cliente_nome}, seu pedido #{numero_pedido} está sendo preparado! ⏳",
  },
  em_separacao: {
    label: "Em Separação",
    template: "{cliente_nome}, seu pedido #{numero_pedido} está sendo separado! 📦",
  },
  pronto: {
    label: "Pronto",
    template: "{cliente_nome}, seu pedido #{numero_pedido} está pronto! ✅",
  },
  aguardando_retirada: {
    label: "Disp. p/ retirada",
    template: "{cliente_nome}, seu pedido #{numero_pedido} está disponível para retirada! Estamos te esperando. 🏪",
  },
  enviado: {
    label: "Saiu p/ entrega",
    template: "{cliente_nome}, seu pedido #{numero_pedido} saiu para entrega! 🛵",
  },
  entregue: {
    label: "Entregue",
    template: "{cliente_nome}, seu pedido #{numero_pedido} foi entregue! Obrigado pela preferência. ❤️",
  },
  avisou_motoboy: {
    label: "Avisou Motoboy (mensagem para o motoboy)",
    template: `Olá {motoboy_nome}! Pedido *#{numero_pedido}* pronto para entrega! 🏍️

*Cliente:* {cliente_nome}
*Tel:* {cliente_whatsapp}

*Endereço:*
{endereco}

*Itens:*
{itens}

*Forma de pagamento:* {pagamento}
*Valor a cobrar:* {valor_cobrar}
{troco}
{obs}`,
  },
  cancelado: {
    label: "Cancelado",
    template: "{cliente_nome}, infelizmente seu pedido #{numero_pedido} foi cancelado. Entre em contato conosco se tiver dúvidas.",
  },
  enviar_frete: {
    label: "Enviar frete",
    template: "Olá {cliente_nome}! Sobre o seu pedido #{numero_pedido}:\n\nProdutos: {total_produtos}\nFrete: {frete}\nTotal atualizado: {total_atualizado}\n{troco}\n\nQualquer dúvida, é só chamar. Obrigado!",
  },
  pedido_alterado: {
    label: "Pedido alterado",
    template: EDITED_ORDER_MESSAGE_DEFAULT,
  },
};

// Mensagem gravada sem {troco} sai com a linha depois do total (comLinhaDoTroco, como no Orders.tsx): o padrão antigo
// gravado sai igual ao novo e não é "personalizado".
const ANCORA_DO_TROCO: Record<string, string> = { enviar_frete: "{total_atualizado}", avisou_motoboy: "{valor_cobrar}" };
// Até 27/10/2026 o padrão de «Saiu para entrega» tinha um caminhão (Pedro: «Vamos trocar logo o padrão para um emoji de
// moto»). A tela salva o padrão junto, então o antigo gravado vale como o padrão novo.
const ENVIADO_ANTIGO = "{cliente_nome}, seu pedido #{numero_pedido} saiu para entrega! 🚚";
const comoSai = (statusId: string, template: string) =>
  statusId === "enviado" && template === ENVIADO_ANTIGO
    ? STATUS_MESSAGE_DEFAULTS.enviado.template
    : ANCORA_DO_TROCO[statusId] ? comLinhaDoTroco(template, ANCORA_DO_TROCO[statusId]) : template;

function ThemeGallery({
  currentThemeName,
  currentColors,
  onSelect,
  applying,
}: {
  currentThemeName: string | null;
  currentColors: { primary: string; secondary: string; accent: string };
  onSelect: (theme: Theme) => void;
  applying: string | null;
}) {
  const exactMatch = THEMES.find(
    (t) =>
      t.primary.toLowerCase() === (currentColors.primary || "").toLowerCase() &&
      t.secondary.toLowerCase() === (currentColors.secondary || "").toLowerCase() &&
      t.accent.toLowerCase() === (currentColors.accent || "").toLowerCase(),
  );
  const knownTheme = currentThemeName ? THEMES.find((t) => t.name === currentThemeName) : null;
  const closest = !exactMatch && !knownTheme ? findClosestTheme(currentColors.primary) : null;
  const isCustom =
    !!currentColors.primary &&
    !exactMatch &&
    !knownTheme &&
    !closest;

  const selectedName = isCustom
    ? CUSTOM_THEME_NAME
    : (exactMatch?.name ?? knownTheme?.name ?? closest?.name ?? null);

  const renderCard = (theme: Theme, isApplying: boolean, isSelected: boolean) => (
    <button
      key={theme.name}
      type="button"
      onClick={() => onSelect(theme)}
      disabled={isApplying}
      className={`relative rounded-xl border-2 p-3 text-left transition-all cursor-pointer ${
        isSelected ? "border-accent ring-2 ring-accent/30 shadow-md" : "border-border hover:border-accent/40"
      } ${isApplying ? "opacity-60" : ""}`}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow">
          <Check className="w-4 h-4" />
        </div>
      )}
      <div className="flex gap-1.5 mb-2">
        <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: theme.primary }} />
        <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: theme.secondary }} />
        <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: theme.accent }} />
      </div>
      <div className="text-sm font-semibold leading-tight pr-6">{theme.name}</div>
    </button>
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {THEMES.map((t) =>
        renderCard(t, applying === t.name, selectedName === t.name),
      )}
      {isCustom && currentColors.primary && (
        <div
          className="relative rounded-xl border-2 border-accent ring-2 ring-accent/30 shadow-md p-3 text-left"
        >
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow">
            <Check className="w-4 h-4" />
          </div>
          <div className="flex gap-1.5 mb-2">
            <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: currentColors.primary }} />
            <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: currentColors.secondary }} />
            <div className="h-10 flex-1 rounded-md" style={{ backgroundColor: currentColors.accent }} />
          </div>
          <div className="text-sm font-semibold leading-tight pr-6">Personalizado</div>
        </div>
      )}
    </div>
  );
}

function PromotionBadgePicker({
  form,
  setForm,
}: {
  form: any;
  setForm: (f: any) => void;
}) {
  const presets = ["Promo", "Promoção", "Oferta"];
  const stored = (form.promotionBadgeText ?? null) as string | null;
  const isPreset = stored !== null && presets.includes(stored);
  const isCustom = stored !== null && !isPreset;
  const selectedPreset = isPreset ? stored : (stored === null ? "Promo" : null);

  const lastCustomRef = useRef<string>("");
  const [shouldFocus, setShouldFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isCustom && stored) lastCustomRef.current = stored;
  }, [stored, isCustom]);

  useEffect(() => {
    if (isCustom && shouldFocus && inputRef.current) {
      inputRef.current.focus();
      const v = inputRef.current.value;
      inputRef.current.setSelectionRange(v.length, v.length);
      setShouldFocus(false);
    }
  }, [isCustom, shouldFocus]);

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold"><Sparkles className="w-5 h-5 text-accent" /> Pílula de Promoção</div>
      <div className="space-y-2">
        <Label>Texto exibido no produto em promoção</Label>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setForm({ ...form, promotionBadgeText: p })}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${selectedPreset === p ? "bg-accent text-accent-foreground border-accent" : "bg-background text-muted-foreground border-border hover:border-accent/50"}`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              if (!isCustom) {
                setForm({ ...form, promotionBadgeText: lastCustomRef.current || "" });
                setShouldFocus(true);
              }
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${isCustom ? "bg-accent text-accent-foreground border-accent" : "bg-background text-muted-foreground border-border hover:border-accent/50"}`}
          >
            Outro
          </button>
        </div>
        {isCustom && (
          <Input
            ref={inputRef}
            value={stored ?? ""}
            maxLength={20}
            onChange={(e) => setForm({ ...form, promotionBadgeText: e.target.value })}
            placeholder="Ex: Imperdível"
            className="mt-2"
          />
        )}
        <p className="text-xs text-muted-foreground">Aparece como pílula colorida no card do produto em promoção (até 20 caracteres).</p>
      </div>
    </Card>
  );
}

// Instalações do app de impressão registradas no servidor (token exchange).
// "visto agora / há 5 min / há 3h / há 2 dias" — sem dep, granularidade de
// suporte (o exchange renova a cada ~15min, precisão fina não agrega).
function lastSeenLabel(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 2) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h}h`;
  return `há ${Math.floor(h / 24)} dias`;
}

export default function Settings() {
  const { data: settings, isLoading } = useSettings();
  const [form, setForm] = useState<Partial<StoreSettings>>({});
  // «Por bairro» sem nenhum bairro com nome e sem aceitar não cadastrado deixa o
  // cliente sem como pedir entrega (315 lojas assim em 14/10/2026). O clique abre a
  // lista, mas o tipo só passa a valer (e a ser salvo) quando a entrega tem saída.
  const [porBairroPendente, setPorBairroPendente] = useState(false);
  // Texto editável das vias de impressão (permite apagar/digitar livre; o número
  // normalizado vai pro form.printCopies). Ver bloco "Papel e impressão".
  const [printCopiesText, setPrintCopiesText] = useState("1");
  const [googleMapsKey, setGoogleMapsKey] = useState<string>("");
  useEffect(() => {
    // Em prod com subdomínio split, precisa bater em api.vendanozap.app
    // (VITE_API_URL); BASE_URL daria /api/* relativo que vai pro Worker.
    const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
    const apiBase = fromEnv
      ? fromEnv.replace(/\/+$/, "")
      : (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
    fetch(`${apiBase}/api/catalog/config/maps-key`)
      .then((r) => r.json())
      .then((d) => { if (d?.key) setGoogleMapsKey(d.key); })
      .catch(() => {});
  }, []);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [showManageSub, setShowManageSub] = useState(false);
  // A pílula «Assinatura» leva ao card do plano pago já aberto, com o «Cancelar Assinatura» à vista (Rey do lanche,
  // 04/11/2026: no plano pago a pílula não aparecia, e o lojista não achava onde cancelar).
  useEffect(() => {
    const abrir = (e: Event) => {
      if ((e as CustomEvent<string>).detail === "cfg-assinatura") setShowManageSub(true);
    };
    window.addEventListener(EVENTO_DA_SECAO, abrir);
    return () => window.removeEventListener(EVENTO_DA_SECAO, abrir);
  }, []);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showQueueDialog, setShowQueueDialog] = useState(false);
  const [showPrintAgentSuggestion, setShowPrintAgentSuggestion] = useState(false);
  // Loja que já imprime pelo app do Windows não precisa do USB do navegador —
  // oferecer os dois confundia (Oxe, 07/10: o "Conectar Impressora" abria a
  // janela do navegador repetidas vezes com o app já funcionando).
  // Aparelhos com o app de impressão (Windows/Android) vistos nos últimos 7 dias. Uma busca só para a chave
  // ["print-agent-devices"]: um useQuery próprio aqui guardava { devices } na mesma chave em que o hook guarda a
  // lista, e quando a busca dele ficava por último o .some() abaixo derrubava a página («M.some is not a function»).
  const { data: agentDevices = [] } = usePrintAgentDevices();
  const appWindowsAberto = agentDevices.some((d) => d.os === "win32" && deviceRecente(d));
  const [queueDialogBusy, setQueueDialogBusy] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugCortado, setSlugCortado] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const slugInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  // «Mensagem no rodapé do cupom» (Profissional): sem o recurso a caixa é só leitura; o selo é para quem não assina.
  const podeRodape = useFeatureAllowed("receipt_footer");
  const podeTaxaDespesa = useFeatureAllowed("delivery_fee_expense");
  const podeTaxaMaquininha = useFeatureAllowed("card_fee_expense");
  const podeTaxaServicoDespesa = useFeatureAllowed("service_fee_expense");
  const pagaPlano = useIsPaidPlan();
  // O texto da caixa enquanto ela edita (e o com aviso, que fica até ela corrigir): fora do formulário, o salvamento
  // automático só recebe o que sai na impressora, já como é gravado, e não fica em «Salvando…» com o que não vai.
  const [rascunhoRodape, setRascunhoRodape] = useState<string | null>(null);
  // Faixa recusada agora (dia-índice, ou dia-add do «+ Adicionar Horário») e o porquê: o campo volta ao valor de antes e
  // o aviso fica até a próxima troca válida.
  const [faixaRecusada, setFaixaRecusada] = useState<{ chave: string; msg: string } | null>(null);
  useEffect(() => {
    if (!porBairroPendente) return;
    const semSaida = entregaPorBairroSemSaida({
      deliveryFeeType: "neighborhood",
      deliveryNeighborhoods: form.deliveryNeighborhoods,
      acceptUnlistedNeighborhoods: form.acceptUnlistedNeighborhoods,
    });
    if (semSaida) return;
    setPorBairroPendente(false);
    setForm((f) => {
      const bairro = f.manualAddressFields?.neighborhood;
      return {
        ...f,
        deliveryFeeType: "neighborhood",
        ...(bairro && !bairro.enabled
          ? { manualAddressFields: { ...f.manualAddressFields, neighborhood: { enabled: true, required: false } } }
          : {}),
      } as Partial<StoreSettings>;
    });
  }, [porBairroPendente, form.deliveryNeighborhoods, form.acceptUnlistedNeighborhoods]);
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const push = usePushNotifications();
  // O aviso do iPhone muda com o jeito que ele abriu o painel (T-0007, 31/10/2026).
  const tipoIphone = tipoDesteIphone();
  const [testingPush, setTestingPush] = useState(false);
  const [soundMode, setSoundMode] = useState<OrderSoundMode>(() => getOrderSoundMode());
  // Feature paga liberada: Ultra OU direito adquirido (a loja já usava quando
  // virou paga — fica na LOJA, então desligar e religar continua podendo).
  const podeFeature = (f: string) =>
    isUltra(settings?.plan) || (((settings as { featureGrants?: string[] } | undefined)?.featureGrants) || []).includes(f);
  // O selo é informação, não bloqueio: aparece pra TODO mundo que não assina o
  // Ultra — inclusive quem tem direito adquirido —, pra ninguém descobrir só
  // ao perder a assinatura que a funcionalidade era de lá (Pedro, 11/09).
  const mostrarSelo = !isUltra(settings?.plan);
  const printer = useThermalPrinter();
  const [testingPrint, setTestingPrint] = useState(false);
  // Texto CRU do "Todo dia começar no número" enquanto o campo está em edição
  // (null = mostra o valor salvo). Sem isso, apagar pra redigitar salvava o
  // vazio como 1 e o número escolhido sumia sem aviso.
  const [dailyStartText, setDailyStartText] = useState<string | null>(null);
  const [diaLimiteText, setDiaLimiteText] = useState<string | null>(null);
  // Limite de encomendas por dia na tela. Servidor antigo não manda o N (o booleano ligado = 1); o form só ganha o
  // orderDayLimit quando o lojista mexe, senão o auto-save regravaria a seção ao abrir.
  const diaLimite: number | null =
    form.orderDayLimit !== undefined ? form.orderDayLimit ?? null : form.orderDayExclusive ? 1 : null;
  const [ddi, setDdi] = useState("55");
  const [loginDdi, setLoginDdi] = useState("55");
  // Verificação de posse de WhatsApp de outra conta (conta multi-loja): o PUT
  // responde WHATSAPP_NEEDS_VERIFICATION e abrimos o modal de código em vez do
  // toast de erro.
  const [waVerify, setWaVerify] = useState<{ number: string; ddi: string; target: "store" | "login" } | null>(null);
  // O motivo do erro no selo do salvar automático, no lugar do «Erro ao salvar» (por ora, só o login de outra conta).
  const [motivoDoErro, setMotivoDoErro] = useState<string | null>(null);

  const searchStr = useSearch();
  const [, setLocation] = useLocation();
  const initializedRef = useRef(false);
  const lastScrolledHashRef = useRef<string>("");

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
    if (!settings) return;
    const runScroll = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      if (lastScrolledHashRef.current === hash) return;
      const tryScroll = (attempts: number) => {
        const el = document.getElementById(hash);
        if (el) {
          lastScrolledHashRef.current = hash;
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Re-anchor after layout settles (handles late content/images shifting position)
          setTimeout(() => {
            const el2 = document.getElementById(hash);
            if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 450);
          setTimeout(() => {
            const el3 = document.getElementById(hash);
            if (el3) el3.scrollIntoView({ behavior: "auto", block: "start" });
          }, 1100);
        } else if (attempts > 0) {
          setTimeout(() => tryScroll(attempts - 1), 80);
        }
      };
      tryScroll(40);
    };
    runScroll();
    const onHash = () => { lastScrolledHashRef.current = ""; runScroll(); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [settings]);
  useEffect(() => {
    if (!settings) return;
    if (initializedRef.current) return;
    const paymentKeyMap: Record<string, string> = {
      dinheiro: "Dinheiro",
      pix: "Pix",
      debito: "Débito",
      credito: "Crédito",
      débito: "Débito",
      crédito: "Crédito",
    };
    const normalizedPayments = (settings.acceptedPayments || []).map((p: string) => {
      const known = paymentMethods.find(m => m.key === p);
      if (known) return p;
      return paymentKeyMap[p.toLowerCase()] ?? p;
    });
    let displayNumber = (settings as any).whatsappFormattedNumber || "";
    const rawFull = (settings.whatsappNumber || "").replace(/\D/g, "");
    if (!displayNumber && rawFull) {
      const knownDdis = ["351", "595", "598", "55", "54", "56", "57", "34", "39", "44", "49", "33", "81", "1"];
      let detectedDdi = "55";
      for (const d of knownDdis) {
        if (rawFull.startsWith(d) && rawFull.length > d.length + 8) {
          detectedDdi = d;
          break;
        }
      }
      setDdi(detectedDdi);
      const localDigits = rawFull.startsWith(detectedDdi) ? rawFull.slice(detectedDdi.length) : rawFull;
      displayNumber = formatWhatsApp(localDigits);
    } else if ((settings as any).whatsappDdi) {
      setDdi((settings as any).whatsappDdi);
    }
    // Login: mesma detecção de DDI, sobre o número da CONTA.
    let loginDisplay = (settings as any).loginWhatsappFormattedNumber || "";
    const loginRaw = ((settings as any).loginWhatsappNumber || "").replace(/\D/g, "");
    if (!loginDisplay && loginRaw) {
      const knownDdis = ["351", "595", "598", "55", "54", "56", "57", "34", "39", "44", "49", "33", "81", "1"];
      let detected = "55";
      for (const d of knownDdis) {
        if (loginRaw.startsWith(d) && loginRaw.length > d.length + 8) { detected = d; break; }
      }
      setLoginDdi(detected);
      loginDisplay = formatWhatsApp(loginRaw.startsWith(detected) ? loginRaw.slice(detected.length) : loginRaw);
    }
    setForm({
      ...settings,
      acceptedPayments: normalizedPayments,
      whatsappNumber: displayNumber,
      loginWhatsappNumber: loginDisplay,
      // null do banco = default 1; o campo sempre mostra um número ao carregar.
      dailyOrderStart: (settings as any).dailyOrderStart ?? 1,
    });
    setPrintCopiesText(String((settings as any).printCopies ?? 1));
    setSlugAvailable(null);
    initializedRef.current = true;
  }, [settings]);

  const checkSlugAvailability = async (valor: string): Promise<boolean | null> => {
    // O campo guarda o link com a barra da frente («/minha-loja»). Com ela, a comparação com o link atual nunca batia
    // e a rota virava /api/auth/check-slug//minha-loja (404): o «Link disponível!» / «em uso» nunca aparecia.
    const slug = valor.replace(/^\/+/, "");
    if (!slug || slug.length < 2) {
      setSlugAvailable(null);
      return null;
    }
    if (settings && slug === settings.slug) {
      setSlugAvailable(true);
      return true;
    }
    setCheckingSlug(true);
    try {
      const exclude = settings?.id ? `?exclude=${settings.id}` : "";
      const data = await customFetch<{ available: boolean }>(`/api/auth/check-slug/${slug}${exclude}`, { method: "GET" });
      setSlugAvailable(data.available);
      if (!data.available) {
        slugInputRef.current?.focus();
      }
      return data.available;
    } catch {
      setSlugAvailable(null);
      return null;
    } finally {
      setCheckingSlug(false);
    }
  };

  // Link da Loja: salva só ao sair do campo, nunca no meio da digitação. O salvamento automático mandava o pedaço
  // («acai-» → «acai»): 409 com «Erro ao salvar», ou um link a mais no store_slug_history. Vai por fora dele e com
  // keepalive, porque o hook aborta o que está em voo quando a página desmonta (outro menu logo depois do campo).
  const linkSalvoRef = useRef<string | null>(null);
  const linkEmEnvioRef = useRef<string | null>(null);
  useEffect(() => { if (settings?.slug) linkSalvoRef.current = settings.slug; }, [settings?.slug]);

  // `conferir`: checa se está livre antes (ao sair do campo). Ao sair da página não dá tempo: manda direto (mesmo
  // com a checagem do campo ainda no ar), e o servidor recusa link em uso. Um envio por link: o que já foi ou está
  // indo não vai de novo.
  const salvarLink = async (slug: string, conferir: boolean) => {
    const jaFoi = () => slug === linkSalvoRef.current || slug === linkEmEnvioRef.current;
    if (slug.length < 2 || jaFoi()) return;
    if (conferir && ((await checkSlugAvailability(slug)) !== true || jaFoi())) return;
    linkEmEnvioRef.current = slug;
    try {
      const resp = await customFetch<any>("/api/settings", { method: "PUT", body: JSON.stringify({ slug }), keepalive: true });
      linkSalvoRef.current = slug;
      queryClient.setQueryData(["settings"], (prev: any) => (prev ? { ...prev, ...resp } : resp));
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    } catch (err) {
      toast({ title: "Erro ao salvar o link", description: extractApiErrorMessage(err), variant: "destructive" });
    } finally {
      if (linkEmEnvioRef.current === slug) linkEmEnvioRef.current = null;
    }
  };

  const handleStoreNameChange = (value: string) => {
    // Nome e slug são independentes: trocar o nome NÃO mexe no link da loja.
    setForm((prev) => ({ ...prev, name: value }));
  };

  const handleStoreNameBlur = () => {};

  const handleSlugChange = (value: string) => {
    const { slug, cortou } = slugDoCampo(value);
    setSlugCortado(cortou);
    setSlugManuallyEdited(true);
    setForm((prev) => ({ ...prev, slug: "/" + slug }));
    setSlugAvailable(null);
  };

  const handleSlugBlur = () => {
    // Ao sair do campo, sai o tracinho do começo e do fim, como o servidor grava; checa e, livre, salva.
    const atual = (form.slug || "").replace(/^\/+/, "");
    const slug = atual.replace(/^-+|-+$/g, "");
    if (slug !== atual) setForm((prev) => ({ ...prev, slug: "/" + slug }));
    if (!slug) return;
    if (slug === linkSalvoRef.current) void checkSlugAvailability(slug);
    else void salvarLink(slug, true);
  };

  // Saiu da página sem sair do campo (outro menu, outra aba, fechou o app): o que foi digitado não se perde.
  const linkPendenteRef = useRef<() => void>(() => {});
  linkPendenteRef.current = () => {
    const slug = (form.slug || "").replace(/^\/+/, "").replace(/^-+|-+$/g, "");
    if (slugAvailable !== false) void salvarLink(slug, false);
  };
  useEffect(() => {
    const aoEsconder = () => { if (document.visibilityState === "hidden") linkPendenteRef.current(); };
    const aoSair = () => linkPendenteRef.current();
    document.addEventListener("visibilitychange", aoEsconder);
    window.addEventListener("pagehide", aoSair);
    return () => {
      document.removeEventListener("visibilitychange", aoEsconder);
      window.removeEventListener("pagehide", aoSair);
      linkPendenteRef.current();
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(searchStr);
    if (params.get("subscribed") === "true") {
      Promise.resolve()
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["settings"] });
          toast({ title: "Sucesso!", description: "Sua assinatura foi ativada com sucesso." });
        })
        .catch(() => {});
      setLocation("/configuracoes", { replace: true });
    }
  }, [searchStr]);

  useEffect(() => {
    if (false) {
      Promise.resolve<{ plan?: string }>({ plan: undefined })
        .then((data: { plan?: string }) => {
          if (data.plan === "profissional") {
            queryClient.invalidateQueries({ queryKey: ["settings"] });
            toast({ title: "Sucesso!", description: "Sua assinatura foi ativada com sucesso." });
          }
        })
        .catch(() => {});
    }
  }, [settings?.plan]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        queryClient.invalidateQueries({ queryKey: ["settings"] });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [queryClient]);

  const ddiRef = useRef(ddi);
  useEffect(() => { ddiRef.current = ddi; }, [ddi]);
  const loginDdiRef = useRef(loginDdi);
  useEffect(() => { loginDdiRef.current = loginDdi; }, [loginDdi]);

  const formForAutoSave = form as Record<string, any>;
  // Ligou o interruptor da pausa e o salvamento ainda não voltou (o aviso sai no onSaved).
  const pausouPeloBotao = useRef(false);
  const [avisoDePausa, setAvisoDePausa] = useState(false);

  const autoSave = useSectionAutoSave({
    form: formForAutoSave,
    serverData: settings as any,
    sections: AUTO_SAVE_SECTIONS,
    endpoint: "/api/settings",
    method: "PUT",
    enabled: initializedRef.current && !!settings,
    debounceMs: 700,
    queryKey: ["settings"],
    buildPayload: (section, fields, currentForm) => {
      const payload: Record<string, any> = {};
      for (const f of fields) payload[f] = currentForm[f];
      // Lista que deixaria a entrega sem saída (ex.: apagou o nome do único bairro
      // para renomear) não vai: o que está salvo continua valendo até ter um nome.
      if (section === "delivery" && settings && entregaPorBairroSemSaida(currentForm) && !entregaPorBairroSemSaida(settings as any)) {
        delete payload.deliveryFeeType;
        delete payload.deliveryNeighborhoods;
        delete payload.acceptUnlistedNeighborhoods;
      }
      // Só vai o que a pessoa mexeu (fields); estes andam junto:
      const composedAddress = [
        currentForm.storeStreet && currentForm.storeNumber
          ? `${currentForm.storeStreet}, ${currentForm.storeNumber}`
          : currentForm.storeStreet,
        currentForm.storeComplement,
        currentForm.storeNeighborhood,
        currentForm.storeCity,
        currentForm.storeState,
      ].filter(Boolean).join(", ");
      if (section === "general") {
        // número + DDI (o servidor monta o número com o DDI que vier). Número com menos de 8 dígitos não vai (Pedro,
        // 29/10/2026): o servidor recusa e o PUT inteiro caía, levando junto o que mais foi mexido na seção (o nome da
        // loja) e mostrando «Erro ao salvar» a cada pausa da digitação.
        if ("whatsappNumber" in payload || "whatsappDdi" in payload) {
          payload.whatsappNumber = currentForm.whatsappNumber;
          payload.whatsappDdi = ddiRef.current;
          if (digitosDoNumero(currentForm.whatsappNumber) < 8) {
            delete payload.whatsappNumber;
            delete payload.whatsappDdi;
          }
        }
        if ("loginWhatsappNumber" in payload || "loginWhatsappDdi" in payload) {
          payload.loginWhatsappNumber = currentForm.loginWhatsappNumber;
          payload.loginWhatsappDdi = loginDdiRef.current;
          if (digitosDoNumero(currentForm.loginWhatsappNumber) < 8) {
            delete payload.loginWhatsappNumber;
            delete payload.loginWhatsappDdi;
          }
        }
        if ("address" in payload && composedAddress) payload.address = composedAddress;
      }
      // endereço da loja → address
      if (section === "delivery" && ENDERECO_DA_LOJA.some((f) => f in payload) && composedAddress) {
        payload.address = composedAddress;
      }
      // mesa ligada + quantidade
      if (section === "mesa" && "tableOrdersEnabled" in payload) payload.tableCount = currentForm.tableCount;
      return payload;
    },
    onError: (section, err: any) => {
      if (err?.status === 401) return;
      setMotivoDoErro(null);
      // Número de PEDIDOS que pertence a outra conta: em vez do erro, abre o
      // fluxo de verificação por código (confirma posse → aplica no servidor).
      if (err?.data?.error?.code === "WHATSAPP_NEEDS_VERIFICATION") {
        const isLogin = err?.data?.error?.details?.target === "login";
        setWaVerify(
          isLogin
            ? { number: formForAutoSave.loginWhatsappNumber || "", ddi: loginDdiRef.current, target: "login" }
            : { number: formForAutoSave.whatsappNumber || "", ddi: ddiRef.current, target: "store" },
        );
        return;
      }
      // WhatsApp de login que já é o login de outra conta (Pedro, 22/10/2026, Sabor na Chapa): o motivo vai no aviso e no
      // selo. Com só «Erro ao salvar», nem a lojista nem a Atendente sabiam o porquê.
      const doServidor = err?.data?.error;
      if (err?.status === 409 && doServidor?.code === "CONFLICT" && section === "general" &&
          typeof doServidor.message === "string" && doServidor.message.includes("login de outra conta")) {
        setMotivoDoErro(doServidor.message);
        toast({ title: doServidor.message, variant: "destructive" });
        return;
      }
      // Rodapé do cupom recusado pelo servidor (o plano ou o texto): o motivo no aviso e no selo.
      if (section === "impressora" && (err?.status === 400 || err?.status === 403) && typeof doServidor?.message === "string") {
        setMotivoDoErro(doServidor.message);
        toast({ title: doServidor.message, variant: "destructive" });
        return;
      }
      toast({
        title: "Erro ao salvar",
        description: err?.message || "Não foi possível salvar. Tente novamente.",
        variant: "destructive",
      });
    },
    onSaved: (_section, salvo) => {
      setMotivoDoErro(null);
      // Pausou pelo «Fechar Loja Temporariamente»: o aviso no meio da tela sai quando a pausa foi gravada (Pedro, 28/10/2026:
      // «um popup que abre no meio da tela», para a loja ter consciência do que fez; no canto, passava despercebido).
      if (pausouPeloBotao.current && salvo?.isActive === false) {
        pausouPeloBotao.current = false;
        setAvisoDePausa(true);
      }
      // A navbar (ex.: item "Caixa"), os gates de plano/tipo e o fuso leem do
      // user.store (getMe), não de ["settings"]. Sem isso, toggles como o do
      // caixa só refletem na navegação após um reload manual.
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    },
  });

  // Número retido (menos de 8 dígitos, diferente do gravado): o selo não diz «Salvo» enquanto ele não for gravado.
  const numeroRetido = (digitado: unknown, gravado: unknown) => {
    const d = digitosDoNumero(digitado);
    return d < 8 && (d > 0 || !!gravado);
  };
  const autoSaveStatus =
    autoSave.status === "saved" &&
    (numeroRetido(form.whatsappNumber, (settings as any)?.whatsappNumber) ||
      numeroRetido(form.loginWhatsappNumber, (settings as any)?.loginWhatsappNumber))
      ? "idle"
      : autoSave.status;

  useEffect(() => {
    if (!initializedRef.current || !settings) return;
    // Campos mudados por fora e não mexidos aqui entram no formulário — senão o próximo salvamento da seção os
    // desfazia com o valor de quando a página abriu (ver acceptServerSnapshot).
    const rebase = autoSave.acceptServerSnapshot(settings as any);
    if (Object.keys(rebase).length > 0) setForm((prev) => ({ ...prev, ...rebase }));
  }, [settings]);

  useEffect(() => {
    if (!initializedRef.current) return;
    setForm((prev) => {
      if ((prev as any).whatsappDdi === ddi) return prev;
      return { ...prev, whatsappDdi: ddi };
    });
  }, [ddi]);
  useEffect(() => {
    if (!initializedRef.current) return;
    setForm((prev) => {
      if ((prev as any).loginWhatsappDdi === loginDdi) return prev;
      return { ...prev, loginWhatsappDdi: loginDdi };
    });
  }, [loginDdi]);

  const logoUploadSeqRef = useRef(0);
  const currentLogoFileRef = useRef<File | null>(null);

  const logoUploadMutation = useMutation({
    mutationFn: async ({ file, seq }: { file: File; seq: number }) => {
      const fd = new FormData();
      fd.append("file", file);
      const res: any = await customFetch("/api/upload/logo", {
        method: "POST",
        body: fd,
        headers: {},
      });
      return { url: res.url as string, seq };
    },
    onSuccess: ({ url, seq }) => {
      if (seq !== logoUploadSeqRef.current) return;
      setForm((prev) => ({ ...prev, logoUrl: url }));
      currentLogoFileRef.current = null;
      setLogoFile(null);
    },
    onError: (err: any, vars) => {
      if (vars && vars.seq !== logoUploadSeqRef.current) return;
      if (err?.status === 401) return;
      toast({
        title: "Erro ao enviar logo",
        description: extractApiErrorMessage(err, "Tente novamente."),
        variant: "destructive",
      });
      currentLogoFileRef.current = null;
      setLogoFile(null);
    },
  });

  useEffect(() => {
    if (!logoFile) return;
    if (currentLogoFileRef.current === logoFile) return;
    currentLogoFileRef.current = logoFile;
    logoUploadSeqRef.current += 1;
    const seq = logoUploadSeqRef.current;
    logoUploadMutation.mutate({ file: logoFile, seq });
  }, [logoFile]);

  const applyThemeMutation = useMutation({
    mutationFn: async (theme: Theme) => {
      return customFetch("/api/settings", {
        method: "PUT",
        body: JSON.stringify({
          themeName: theme.name,
          colorPrimary: theme.primary,
          colorSecondary: theme.secondary,
          colorAccent: theme.accent,
        }),
      });
    },
    onSuccess: (_data, theme) => {
      const existingSettings = queryClient.getQueryData<any>(["settings"]);
      if (existingSettings) {
        queryClient.setQueryData(["settings"], {
          ...existingSettings,
          themeName: theme.name,
          colorPrimary: theme.primary,
          colorSecondary: theme.secondary,
          colorAccent: theme.accent,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      const meKey = getGetMeQueryKey();
      const current = queryClient.getQueryData<any>(meKey);
      if (current?.store) {
        queryClient.setQueryData(meKey, {
          ...current,
          store: {
            ...current.store,
            colorPrimary: theme.primary,
            colorSecondary: theme.secondary,
            colorAccent: theme.accent,
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: meKey });
      toast({ title: `Tema aplicado: ${theme.name}` });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao aplicar tema", description: err?.message, variant: "destructive" });
    },
  });

  const copyLink = (url: string, key: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(key);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  if (isLoading || !settings) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const showProducts = form.storeType === "produtos" || form.storeType === "ambos";
  const showServices = form.storeType === "servicos" || form.storeType === "ambos";

  const cleanOrigin = window.location.origin.replace("://www.", "://");
  const catalogUrl = `${cleanOrigin}/loja/${settings.slug}`;
  const agendaUrl = `${cleanOrigin}/agendar/${settings.slug}`;
  const displayUrl = (url: string) => url.replace(/^https?:\/\//, "");

  const togglePayment = (key: string) => {
    const current = form.acceptedPayments || [];
    const next = current.includes(key) ? current.filter((p) => p !== key) : [...current, key];
    setForm({ ...form, acceptedPayments: next });
  };

  // União em ordem canônica (paymentMethods) — mantém acceptedPayments coerente
  // com as listas por modalidade pros consumidores que leem a lista única.
  const unionByMode = (bm: { delivery: string[]; pickup: string[] }) =>
    paymentMethods.filter((m) => bm.delivery.includes(m.key) || bm.pickup.includes(m.key)).map((m) => m.key);

  // Liga/desliga "formas diferentes por modalidade". Ao ligar, pré-preenche as
  // duas com o que a loja aceita hoje; ao desligar, mantém a união na lista única.
  const setPaymentsSplit = (active: boolean) => {
    if (active) {
      const base = form.acceptedPayments || [];
      setForm({ ...form, acceptedPaymentsByMode: { delivery: [...base], pickup: [...base] } });
    } else {
      const bm = form.acceptedPaymentsByMode;
      setForm({
        ...form,
        acceptedPaymentsByMode: null,
        acceptedPayments: bm ? unionByMode(bm) : form.acceptedPayments || [],
      });
    }
  };

  const togglePaymentMode = (mode: "delivery" | "pickup", key: string) => {
    const bm = form.acceptedPaymentsByMode || { delivery: [], pickup: [] };
    const list = bm[mode];
    const nextList = list.includes(key) ? list.filter((p) => p !== key) : [...list, key];
    const nextBm = { ...bm, [mode]: nextList };
    setForm({ ...form, acceptedPaymentsByMode: nextBm, acceptedPayments: unionByMode(nextBm) });
  };

  const updateBH = (day: string, updater: (d: { enabled: boolean; ranges: Array<{ start: string; end: string }> }) => { enabled: boolean; ranges: Array<{ start: string; end: string }> }) => {
    const bh = { ...(form.businessHours || {}) };
    const current = normalizeBH(bh[day]);
    bh[day] = updater(current);
    setForm({ ...form, businessHours: bh });
  };

  return (
    <div id="settings-sections-root" className="space-y-6 pb-20 md:pb-0">
      {waVerify && (
        <WhatsappVerifyModal
          open={!!waVerify}
          onClose={() => setWaVerify(null)}
          whatsappNumber={waVerify.number}
          whatsappDdi={waVerify.ddi}
          target={waVerify.target}
          onVerified={() => {
            const wasLogin = waVerify.target === "login";
            setWaVerify(null);
            queryClient.invalidateQueries({ queryKey: ["settings"] });
            queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
            toast(
              wasLogin
                ? { title: "Login alterado", description: "Da próxima vez, entre com o número novo." }
                : { title: "WhatsApp confirmado", description: "Os pedidos desta loja passam a chegar nesse número." },
            );
          }}
        />
      )}
      <div className="flex justify-end">
        <InstallAppButton />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground mt-1">Personalize sua loja e preferências.</p>
        </div>
      </div>
      <SettingsSectionNav />
      {autoSaveStatus !== "idle" && (
        <div
          aria-live="polite"
          className={`fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 flex items-center gap-2 px-3 py-2 rounded-full max-w-[calc(100vw-2rem)] bg-background/95 backdrop-blur border shadow-lg text-sm transition-opacity ${
            autoSaveStatus === "error" ? "text-red-600 border-red-300" : "text-muted-foreground"
          }`}
        >
          {autoSaveStatus === "saving" && (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Salvando...</span>
            </>
          )}
          {autoSaveStatus === "saved" && (
            <>
              <Check className="w-4 h-4 text-green-600" />
              <span>Salvo</span>
            </>
          )}
          {autoSaveStatus === "error" && (
            <>
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{motivoDoErro || "Erro ao salvar"}</span>
            </>
          )}
        </div>
      )}
      <SectionErrorBoundary anchorId="cfg-inicio" label="Loja">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><StoreIcon className="w-5 h-5 text-primary" /> Loja</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome da loja</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => handleStoreNameChange(e.target.value)}
                onBlur={handleStoreNameBlur}
              />
            </div>
            {!settings.isChildUnit && (
            <div className="space-y-2">
              <Label>Link da Loja</Label>
              <div className="relative">
                <span className="absolute top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium pointer-events-none" style={{ left: '14px' }}>/</span>
                <Input
                  ref={slugInputRef}
                  value={form.slug ? form.slug.replace(/^\//, "") : ""}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  onBlur={handleSlugBlur}
                  className={`pl-6 ${slugAvailable === false ? "border-red-500 focus-visible:ring-red-500" : slugAvailable === true ? "border-green-500 focus-visible:ring-green-500" : ""}`}
                />
              </div>
              {slugCortado && (
                <p className="text-xs text-muted-foreground">Aqui vai só o final do link</p>
              )}
              {slugAvailable === false && (
                <p className="text-xs text-red-500 font-medium">Este link já está em uso. Escolha outro.</p>
              )}
              {slugAvailable === true && (
                <p className="text-xs text-green-600 font-medium">Link disponível!</p>
              )}
              {checkingSlug && (
                <p className="text-xs text-muted-foreground">Verificando disponibilidade...</p>
              )}
            </div>
            )}
            <div className="space-y-2">
              <Label>WhatsApp para pedidos</Label>
              <WhatsAppInput
                value={form.whatsappNumber || ""}
                onChange={(v) => setForm({ ...form, whatsappNumber: v })}
                ddi={ddi}
                onDdiChange={setDdi}
              />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp de login</Label>
              {/* Impersonando, o número é o da DONA da loja e trocar daqui mexeria
                  no login da conta do suporte — por isso só leitura. */}
              <WhatsAppInput
                value={form.loginWhatsappNumber || ""}
                onChange={(v) => setForm({ ...form, loginWhatsappNumber: v })}
                ddi={loginDdi}
                onDdiChange={setLoginDdi}
                disabled={!!(user as any)?.isImpersonating}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              {(form.logoUrl || logoFile) && (
                <div className="relative w-16 h-16">
                  <div className="w-full h-full rounded-xl overflow-hidden bg-muted border">
                    <img src={logoFile ? URL.createObjectURL(logoFile) : form.logoUrl!} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setLogoFile(null);
                      setForm({ ...form, logoUrl: null });
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/90 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <label className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-muted transition-colors text-sm">
                <ImageIcon className="w-4 h-4" /> Escolher logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
        </Card>
      </SectionErrorBoundary>
      <EquipeSection cashRegisterEnabled={!!form.cashRegisterEnabled} />
      <SectionErrorBoundary anchorId="cfg-tipo" label="Tipo de Negócio">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><StoreIcon className="w-5 h-5 text-primary" /> Tipo de Negócio</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { value: "produtos", label: "Apenas Produtos" },
              { value: "servicos", label: "Apenas Serviços" },
              { value: "ambos", label: "Produtos e Serviços" },
            ].map((opt) => (
                <button
                key={opt.value}
                onClick={() => setForm({ ...form, storeType: opt.value })}
                  className={`p-4 rounded-xl border-2 text-center font-medium transition-all cursor-pointer ${
                  form.storeType === opt.value ? "border-accent bg-accent/10 text-foreground" : "border-border hover:border-accent/30"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Card>
      </SectionErrorBoundary>
      <SectionErrorBoundary anchorId="cfg-visual" label="Identidade Visual">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><Palette className="w-5 h-5 text-primary" /> Identidade Visual</div>
          <p className="text-sm text-muted-foreground">Escolha um tema para sua loja. O tema é aplicado imediatamente em todo o sistema.</p>
          <ThemeGallery
            currentThemeName={form.themeName ?? null}
            currentColors={{
              primary: form.colorPrimary || "",
              secondary: form.colorSecondary || "",
              accent: form.colorAccent || "",
            }}
            onSelect={(theme) => {
              setForm((prev) => ({
                ...prev,
                themeName: theme.name,
                colorPrimary: theme.primary,
                colorSecondary: theme.secondary,
                colorAccent: theme.accent,
              }));
              applyThemeMutation.mutate(theme);
            }}
            applying={applyThemeMutation.isPending ? applyThemeMutation.variables?.name ?? null : null}
          />
        </Card>
      </SectionErrorBoundary>
      <hr className="border-border/40" />
      <SectionErrorBoundary anchorId="cfg-horarios" label="Horário de Funcionamento">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><Clock className="w-5 h-5 text-primary" /> Horário de Funcionamento</div>
          <div className="space-y-1.5 pb-3 border-b border-border/50">
            <Label>Fuso horário da loja</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.timezone || "America/Sao_Paulo"}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            >
              {form.timezone &&
                !BRAZIL_TIMEZONES.some((t) => t.id === form.timezone) &&
                !WORLD_TIMEZONES.some((t) => t.id === form.timezone) && (
                  <option value={form.timezone}>{form.timezone}</option>
                )}
              <optgroup label="Brasil">
                {BRAZIL_TIMEZONES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </optgroup>
              {WORLD_TIMEZONES.length > 0 && (
                <optgroup label="Todos os fusos">
                  {WORLD_TIMEZONES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="text-xs text-muted-foreground">
              Vale pra toda a conta: horário de funcionamento, numeração diária dos pedidos, cupons e promoções.
              Agora na loja:{" "}
              <strong className="text-foreground">
                {(() => {
                  try {
                    return new Date().toLocaleTimeString("pt-BR", {
                      timeZone: form.timezone || "America/Sao_Paulo",
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                  } catch {
                    return "--:--";
                  }
                })()}
              </strong>
            </p>
          </div>
          <div className="space-y-3">
            {BH_DAYS.map(({ key, label }) => {
              const bh = normalizeBH(form.businessHours?.[key]);
              return (
                <div key={key} className={`space-y-3 p-3 rounded-lg transition-colors ${bh.enabled ? "border bg-muted/30" : ""}`}>
                  <div className="flex items-center gap-3">
                    <Switch checked={bh.enabled} onCheckedChange={(v) => updateBH(key, (d) => ({ ...d, enabled: v }))} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  {bh.enabled && (
                    <div className="space-y-2">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            // Em minutos, não por posição na lista: com horário
                            // digitado ("21:50") o indexOf não acha nada.
                            const last = bh.ranges[bh.ranges.length - 1];
                            const lastEnd = hmToMinutes(last?.end) ?? hmToMinutes("13:00")!;
                            const nextStart = minutesToHM(lastEnd + BH_STEP);
                            const nextEnd = minutesToHM((hmToMinutes(nextStart) ?? lastEnd) + BH_STEP);
                            // Depois das 23:59 não cabe faixa no mesmo dia (sairia «23:59 às 23:59»).
                            if (problemaDaFaixa(nextStart, nextEnd)) {
                              setFaixaRecusada({ chave: `${key}-add`, msg: "Este dia já vai até 23:59. Depois da meia-noite, use o dia seguinte, a partir de 00:00." });
                              return;
                            }
                            setFaixaRecusada(null);
                            updateBH(key, (d) => ({ ...d, ranges: [...d.ranges, { start: nextStart, end: nextEnd }] }));
                          }}
                          className="text-xs text-primary font-medium hover:underline"
                        >
                          + Adicionar Horário
                        </button>
                      </div>
                      {faixaRecusada?.chave === `${key}-add` && (
                        <p className="text-xs text-red-600 text-right" data-testid={`bh-add-aviso-${key}`}>{faixaRecusada.msg}</p>
                      )}
                      {bh.ranges.map((range, ri) => {
                        // A lista suspensa continua filtrada como antes (só o que
                        // faz sentido depois da faixa anterior / do início), mas
                        // agora é SUGESTÃO: o campo aceita qualquer horário digitado.
                        const prev = ri > 0 ? bh.ranges[ri - 1] : null;
                        const prevEndMin = hmToMinutes(prev?.end ?? null);
                        const startOptions = prevEndMin != null
                          ? bhTimeOptions.filter((t) => (hmToMinutes(t) ?? 0) > prevEndMin)
                          : bhTimeOptions;
                        const startMin = hmToMinutes(range.start);
                        // A faixa como está gravada (só informa, não trava: a API aceita a faixa igual à gravada) ou a
                        // troca que acabou de ser recusada.
                        const problemaGravado = problemaDaFaixa(range.start, range.end);
                        const aviso = faixaRecusada?.chave === `${key}-${ri}` ? faixaRecusada.msg : problemaGravado;
                        const fimMeiaNoite = !!aviso;
                        const endOptions = startMin != null
                          ? bhTimeOptions.filter((t) => (hmToMinutes(t) ?? 0) > startMin)
                          : bhTimeOptions;
                        return (
                        <div key={ri} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <TimeField
                            className="flex-1"
                            value={range.start}
                            options={startOptions}
                            data-testid={`bh-start-${key}-${ri}`}
                            onChange={(newStart) => {
                              // Fim tem que continuar depois do início; empurra
                              // um passo quando o início passa por cima dele.
                              const ni = hmToMinutes(newStart);
                              const ei = hmToMinutes(range.end);
                              const newEnd = ni != null && ei != null && ei <= ni
                                ? minutesToHM(ni + BH_STEP)
                                : range.end;
                              // Início em 23:59 não deixa fim depois dele: a troca não entra.
                              const problema = problemaDaFaixa(newStart, newEnd);
                              if (problema) { setFaixaRecusada({ chave: `${key}-${ri}`, msg: problema }); return; }
                              setFaixaRecusada(null);
                              updateBH(key, (d) => ({
                                ...d,
                                ranges: d.ranges.map((r, j) => (j === ri ? { ...r, start: newStart, end: newEnd } : r)),
                              }));
                            }}
                          />
                          <span className="text-xs text-muted-foreground">às</span>
                          <TimeField
                            className={`flex-1 ${fimMeiaNoite ? "rounded-md ring-2 ring-red-500" : ""}`}
                            value={range.end}
                            options={endOptions}
                            data-testid={`bh-end-${key}-${ri}`}
                            onChange={(newEnd) => {
                              // Fim igual, antes do início ou 00:00 não entra (ver problemaDaFaixa): o campo volta ao
                              // valor de antes e avisa.
                              const problema = problemaDaFaixa(range.start, newEnd);
                              if (problema) { setFaixaRecusada({ chave: `${key}-${ri}`, msg: problema }); return; }
                              setFaixaRecusada(null);
                              updateBH(key, (d) => ({
                                ...d,
                                ranges: d.ranges.map((r, j) => j === ri ? { ...r, end: newEnd } : r),
                              }));
                            }}
                          />
                          {bh.ranges.length > 1 && (
                            <button
                              type="button"
                              onClick={() => updateBH(key, (d) => ({
                                ...d,
                                ranges: d.ranges.filter((_, j) => j !== ri),
                              }))}
                              className="text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {aviso && (
                          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-red-600 -mt-1" data-testid={`bh-end-aviso-${key}-${ri}`}>
                            {aviso}
                            {problemaGravado === MSG_MEIA_NOITE && (hmToMinutes(range.start) ?? 0) < 23 * 60 + 59 && (
                              <button
                                type="button"
                                className="font-semibold underline"
                                data-testid={`bh-end-2359-${key}-${ri}`}
                                onClick={() => {
                                  setFaixaRecusada(null);
                                  updateBH(key, (d) => ({
                                    ...d,
                                    ranges: d.ranges.map((r, j) => j === ri ? { ...r, end: "23:59" } : r),
                                  }));
                                }}
                              >
                                Terminar às 23:59
                              </button>
                            )}
                          </p>
                        )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              <Label htmlFor="fechar-loja">Fechar Loja Temporariamente</Label>
              <p className="text-xs text-muted-foreground">Ative para parar de receber pedidos temporariamente.</p>
            </div>
            {/* Invertido na tela (Pedro, 27/10): ligado = loja pausada. Grava o mesmo is_active. */}
            <Switch id="fechar-loja" checked={form.isActive === false} onCheckedChange={(v) => { pausouPeloBotao.current = v; setForm({ ...form, isActive: !v }); }} data-testid="settings-fechar-loja" />
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="min-w-0 pr-3">
              <Label>Receber pedidos com a loja fechada</Label>
              <p className="text-xs text-muted-foreground">Fora do horário, o cliente ainda envia o pedido e vê o aviso de que será feito quando a loja abrir. Desative para bloquear pedidos fora do horário.</p>
            </div>
            <Switch
              className="shrink-0"
              checked={form.acceptOrdersWhenClosed !== false}
              onCheckedChange={(v) => setForm({ ...form, acceptOrdersWhenClosed: v })}
            />
          </div>
        </Card>
      </SectionErrorBoundary>
      <SectionErrorBoundary anchorId="cfg-pagamentos" label="Formas de Pagamento">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><CreditCard className="w-5 h-5 text-primary" /> Formas de Pagamento</div>

          {!form.acceptedPaymentsByMode ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {paymentMethods.map((pm) => (
                <button
                  key={pm.key}
                  type="button"
                  onClick={() => togglePayment(pm.key)}
                  className={`p-3 rounded-xl border-2 text-center text-sm font-medium transition-all cursor-pointer ${
                    (form.acceptedPayments || []).includes(pm.key) ? "border-accent bg-accent/10 text-foreground" : "border-border hover:border-accent/30"
                  }`}
                >
                  {pm.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              {([
                { mode: "delivery" as const, label: "Entrega", icon: <Truck className="w-4 h-4" /> },
                { mode: "pickup" as const, label: "Retirada", icon: <StoreIcon className="w-4 h-4" /> },
              ]).map(({ mode, label, icon }) => {
                const list = form.acceptedPaymentsByMode?.[mode] || [];
                return (
                  <div key={mode} className="space-y-2">
                    <p className="flex items-center gap-1.5 text-sm font-medium">{icon} {label}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {paymentMethods.map((pm) => (
                        <button
                          key={pm.key}
                          type="button"
                          onClick={() => togglePaymentMode(mode, pm.key)}
                          className={`p-3 rounded-xl border-2 text-center text-sm font-medium transition-all cursor-pointer ${
                            list.includes(pm.key) ? "border-accent bg-accent/10 text-foreground" : "border-border hover:border-accent/30"
                          }`}
                        >
                          {pm.label}
                        </button>
                      ))}
                    </div>
                    {list.length === 0 && (
                      <p className="text-xs text-red-500">Selecione ao menos uma forma de pagamento para {label.toLowerCase()}.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            <div className="min-w-0">
              <Label>Formas diferentes para Entrega e Retirada</Label>
              <p className="text-xs text-muted-foreground">Ex.: aceitar cartão só na entrega (maquininha vai com o motoboy) ou só na retirada na loja.</p>
            </div>
            <Switch checked={!!form.acceptedPaymentsByMode} onCheckedChange={setPaymentsSplit} />
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            <div className="min-w-0">
              <Label>Aceitar pagamento com duas formas</Label>
              <p className="text-xs text-muted-foreground">No link público, o cliente pode dividir o pedido em duas formas (ex.: parte no PIX, parte em dinheiro). O PDV do balcão não depende desta opção.</p>
            </div>
            <Switch
              className="shrink-0"
              checked={!!form.allowMixedPayments}
              onCheckedChange={(v) => setForm({ ...form, allowMixedPayments: v })}
            />
          </div>

          <div className="space-y-2 pt-3 border-t">
            <div className="min-w-0">
              <Label>Crediário (venda a prazo) {mostrarSelo && <UltraBadge className="ml-1 align-middle" />}</Label>
              <p className="text-xs text-muted-foreground">Crie formas de pagamento parceladas com o nome que quiser (ex: "Crediário", "Carnê da loja"). Você define aqui as parcelas e os juros; na venda, elas viram <strong>contas a receber</strong> no Financeiro, com lembrete de vencimento.</p>
            </div>
            {(form.crediarioMethods || []).map((m, idx) => {
              const updateMethod = (patch: Partial<typeof m>) => {
                const next = [...(form.crediarioMethods || [])];
                next[idx] = { ...next[idx], ...patch };
                setForm({ ...form, crediarioMethods: next });
              };
              return (
                <div key={idx} className="rounded-lg border p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={m.name}
                      maxLength={30}
                      placeholder="Nome do crediário (ex: Casas Bahia)"
                      onChange={(e) => updateMethod({ name: e.target.value })}
                      className="flex-1 min-w-0 h-9"
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, crediarioMethods: (form.crediarioMethods || []).filter((_, i) => i !== idx) })}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                      aria-label="Remover crediário"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Máximo de parcelas</Label>
                      <select
                        value={m.installments ?? 2}
                        onChange={(e) => updateMethod({ installments: parseInt(e.target.value, 10) || 1 })}
                        className="w-full border rounded-md p-2 text-sm bg-background h-9"
                      >
                        {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>até {n}x</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-muted-foreground">O cliente escolhe até esse limite.</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo de juros</Label>
                      <select
                        value={m.interestMode === "parcela" ? "parcela" : "total"}
                        onChange={(e) => updateMethod({ interestMode: e.target.value === "parcela" ? "parcela" : "total" })}
                        className="w-full border rounded-md p-2 text-sm bg-background h-9"
                        data-testid="settings-crediario-interest-mode"
                      >
                        <option value="total">Juros no total</option>
                        <option value="parcela">Juros por parcela</option>
                      </select>
                    </div>
                    <div className="space-y-1 col-span-2 sm:col-span-1">
                      <Label className="text-xs">{m.interestMode === "parcela" ? "Juros por parcela (%)" : "Juros no total (%)"}</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        // Vazio = sem juros (0). value com fallback "" deixa
                        // apagar o campo — `?? 0` prendia um zero indeletável.
                        value={m.interestMode === "parcela" ? (m.interestPerInstallment ? m.interestPerInstallment : "") : (m.interestPercent ? m.interestPercent : "")}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const val = raw === "" ? undefined : Math.min(Math.max(Number(raw) || 0, 0), 100);
                          updateMethod(m.interestMode === "parcela" ? { interestPerInstallment: val } : { interestPercent: val });
                        }}
                        placeholder="0"
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {m.interestMode === "parcela"
                          ? "1x sem juros; cada parcela a mais soma esse % (ex: 2% → 3x = 4% no total)."
                          : "Mesmo % em qualquer nº de parcelas."}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Aceitar pedidos pelo link da loja</p>
                      <p className="text-xs text-muted-foreground">Desligado, o crediário só aparece no PDV/balcão. Ligado, o cliente também pode escolher no link (com as parcelas e juros definidos aqui).</p>
                    </div>
                    <Switch
                      className="shrink-0"
                      checked={!!m.allowPublic}
                      onCheckedChange={(v) => updateMethod({ allowPublic: v })}
                    />
                  </div>
                </div>
              );
            })}
            {(form.crediarioMethods || []).length < 5 && podeFeature("crediario") && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm({ ...form, crediarioMethods: [...(form.crediarioMethods || []), { name: "", allowPublic: false, installments: 2, interestPercent: 0 }] })}
                data-testid="settings-add-crediario"
              >
                <Plus className="w-4 h-4 mr-1" /> Adicionar crediário
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            <div className="min-w-0">
              <Label>Abertura e fechamento de caixa {mostrarSelo && <UltraBadge className="ml-1 align-middle" />}</Label>
              <p className="text-xs text-muted-foreground">Ativa a página <strong>Caixa</strong> pra controlar a gaveta de dinheiro por turno: fundo de troco, entradas e retiradas de dinheiro e conferência de sobra/falta no fechamento.</p>
            </div>
            <Switch
              className="shrink-0"
              checked={!!form.cashRegisterEnabled}
              disabled={!podeFeature("cash_register")}
              onCheckedChange={(v) => setForm({ ...form, cashRegisterEnabled: v })}
              data-testid="settings-cash-register"
            />
          </div>
        </Card>
      </SectionErrorBoundary>
      {SURCHARGE_METHODS.some((m) => (form.acceptedPayments || []).includes(m.key)) && (
        <div className="space-y-4">
          {SURCHARGE_METHODS.map((m) => {
            const methodKey = m.key;
            if (!(form.acceptedPayments || []).includes(methodKey)) return null;
            const cfg = form.cardSurchargeConfig?.[methodKey] || { enabled: false, type: "percent" as const, value: 0, includeDelivery: false };
            const updateCfg = (patch: Partial<typeof cfg>) => {
              const next = { ...(form.cardSurchargeConfig || {}) };
              next[methodKey] = { ...cfg, ...patch };
              setForm({ ...form, cardSurchargeConfig: next });
            };
            // Pedro, 25/10/2026 (alternativa C; os vales também): o cartão da forma tem o acréscimo cobrado do cliente e a
            // taxa da maquininha lançada como despesa, cada um na sua chave.
            const idForma = methodKey.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, "-");
            const fee = form.cardFeeExpenseConfig?.[methodKey] || { enabled: false, percent: 0 };
            const updateFee = (patch: Partial<typeof fee>) => {
              // O servidor troca o objeto inteiro: vai a forma mexida junto com as que já estavam lá.
              const next = { ...(form.cardFeeExpenseConfig || {}) };
              next[methodKey] = { ...fee, ...patch };
              setForm({ ...form, cardFeeExpenseConfig: next });
            };
            // Sem o recurso só dá para desligar: a chave já ligada (plano que venceu) continua podendo sair.
            const taxaTravada = !podeTaxaMaquininha && !fee.enabled;
            const chaveDoAcrescimo = (
              <label className="inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={cfg.enabled}
                  onChange={(e) => updateCfg({ enabled: e.target.checked })}
                  aria-label={`Cobrar acréscimo em ${m.label}`}
                  className="sr-only peer"
                  data-testid={`settings-card-surcharge-${idForma}-switch`}
                />
                <div className="relative w-11 h-6 bg-muted rounded-full peer peer-checked:bg-accent transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            );
            return (
              <Card key={methodKey} className="p-6 space-y-4">
                <p className="text-sm sm:text-base font-semibold truncate min-w-0">{m.label}</p>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">Cobrar acréscimo do cliente</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Cobre um percentual ou valor fixo do cliente ao pagar com {m.label}.</p>
                  </div>
                  {chaveDoAcrescimo}
                </div>
                {cfg.enabled && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Tipo</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => updateCfg({ type: "percent" })}
                          className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${cfg.type === "percent" ? "border-accent bg-accent/10" : "border-border hover:border-accent/30"}`}
                        >
                          Percentual (%)
                        </button>
                        <button
                          type="button"
                          onClick={() => updateCfg({ type: "fixed" })}
                          className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${cfg.type === "fixed" ? "border-accent bg-accent/10" : "border-border hover:border-accent/30"}`}
                        >
                          Valor fixo (R$)
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">{cfg.type === "percent" ? "Percentual (%)" : "Valor fixo (R$)"}</Label>
                      {cfg.type === "percent" ? (
                        // Vírgula ou ponto, como o da taxa: o campo de número do navegador em inglês jogava a vírgula fora
                        // («4,5» virava 45%). O valor salvo aparece como está, sem limite.
                        <div className="max-w-[180px]" data-testid={`settings-card-surcharge-${idForma}-percent`}>
                          <NumberInput
                            value={cfg.value === 0 ? null : cfg.value}
                            allowEmpty
                            decimalComma
                            onChange={(n) => updateCfg({ value: n ?? 0 })}
                            placeholder="Ex: 3,5"
                            className="h-10"
                          />
                        </div>
                      ) : (
                        <div className="max-w-[180px]">
                          <CurrencyInput
                            value={cfg.value === 0 ? "" : String(cfg.value)}
                            onChange={(v) => {
                              const n = parseFloat(v);
                              updateCfg({ value: isNaN(n) ? 0 : n });
                            }}
                            className="h-10"
                          />
                        </div>
                      )}
                    </div>
                    {cfg.type === "percent" && (
                      <label className="flex items-center gap-2 cursor-pointer w-full">
                        <input
                          type="checkbox"
                          checked={cfg.includeDelivery}
                          onChange={(e) => updateCfg({ includeDelivery: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 shrink-0 bg-muted rounded-full peer peer-checked:bg-accent transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                        <span className="text-xs font-medium flex-1 min-w-0">Aplicar acréscimo também sobre a taxa de entrega</span>
                      </label>
                    )}
                  </div>
                )}
                <div className="pt-4 border-t border-border space-y-4" data-testid={`settings-card-fee-${idForma}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Label className="text-sm font-medium">
                          Lançar a taxa da maquininha como despesa {!pagaPlano && <UltraBadge plan="profissional" className="ml-1 align-middle" />}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Entra no Financeiro quando o pedido é marcado como Entregue.</p>
                      </div>
                      <label className={`inline-flex items-center shrink-0 ${taxaTravada ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                        <input
                          type="checkbox"
                          checked={fee.enabled}
                          disabled={taxaTravada}
                          onChange={(e) => updateFee({ enabled: e.target.checked })}
                          aria-label={`Lançar a taxa da maquininha de ${m.label} como despesa`}
                          className="sr-only peer"
                          data-testid={`settings-card-fee-${idForma}-switch`}
                        />
                        <div className="relative w-11 h-6 bg-muted rounded-full peer peer-checked:bg-accent transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                      </label>
                    </div>
                    {fee.enabled && (
                      <div className="space-y-2">
                        <Label className="text-xs">Taxa da maquininha (%)</Label>
                        {/* Vírgula ou ponto: o campo de número do navegador em inglês jogava a vírgula fora («3,2» virava
                            32%). De 0 a 100, como o servidor guarda. */}
                        <div className="max-w-[180px]" data-testid={`settings-card-fee-${idForma}-percent`}>
                          <NumberInput
                            value={fee.percent === 0 ? null : fee.percent}
                            allowEmpty
                            decimalComma
                            onChange={(n) => updateFee({ percent: n == null ? 0 : Math.min(100, Math.max(0, n)) })}
                            placeholder="Ex: 3,2"
                            className="h-10"
                          />
                        </div>
                      </div>
                    )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <SectionErrorBoundary label="Pedido Mínimo">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><StoreIcon className="w-5 h-5 text-primary" /> Pedido Mínimo</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Pedido mínimo</Label><CurrencyInput value={form.minOrderValue} onChange={(v) => setForm({ ...form, minOrderValue: v })} placeholder="Opcional" /></div>
          </div>
        </Card>
      </SectionErrorBoundary>
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-entrega" label="Modalidades de Entrega">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Truck className="w-5 h-5 text-primary" /> Modalidades de Entrega</div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-sm font-semibold"><MapPin className="w-4 h-4" /> Endereço da Loja</Label>
                <p className="text-xs text-muted-foreground">Usado para calcular distância de entrega e mostrar ao cliente o local de retirada.</p>
                <AddressAutocomplete
                  googleMapsApiKey={googleMapsKey || settings?.googleMapsApiKey || ""}
                  value={{
                    street: form.storeStreet || "",
                    number: form.storeNumber || "",
                    complement: form.storeComplement || "",
                    neighborhood: form.storeNeighborhood || "",
                    city: form.storeCity || "",
                    state: form.storeState || "",
                    zipCode: form.storeZipCode || "",
                    lat: form.storeLat || null,
                    lng: form.storeLng || null,
                    formattedAddress: "",
                  }}
                  onChange={(addr) => {
                    setForm(prev => ({
                      ...prev,
                      storeStreet: addr.street,
                      storeNumber: addr.number,
                      storeComplement: addr.complement,
                      storeNeighborhood: addr.neighborhood,
                      storeCity: addr.city,
                      storeState: addr.state,
                      storeZipCode: addr.zipCode,
                      storeLat: addr.lat,
                      storeLng: addr.lng,
                    }));
                  }}
                  showMap={true}
                  mapHeight={250}
                  placeholder="Buscar endereço da loja..."
                  inputClassName="bg-muted/30"
                  // Mesma UX do checkout: GPS em destaque (lojista geralmente
                  // está NA loja), busca contraída atrás do toggle, escape
                  // "busca não está funcionando" dentro da área expandida
                  // (substitui o botão manual do rodapé — hideManualToggle).
                  enableGeolocation={true}
                  hideManualToggle={true}
                />
              </div>

              <div className="border-t-[3px] border-dashed border-border pt-4" />

              <div className="flex items-center justify-between">
                <Label>Aceita retirada no local</Label>
                <Switch checked={form.allowsPickup || false} onCheckedChange={(v) => setForm({ ...form, allowsPickup: v })} />
              </div>
              {form.allowsPickup && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Label className="text-sm font-medium">Outras formas de retirada</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        O cliente escolhe entre retirar na loja ou uma forma que você definir (ex: mandar um Uber Flash buscar).
                      </p>
                    </div>
                    <Switch
                      className="mt-0.5 shrink-0"
                      checked={(form.pickupMethods?.length ?? 0) > 0}
                      onCheckedChange={(v) => setForm({ ...form, pickupMethods: v ? [""] : null })}
                      data-testid="settings-pickup-methods-toggle"
                    />
                  </div>
                  {(form.pickupMethods?.length ?? 0) > 0 && (
                    <div className="space-y-2">
                      {(form.pickupMethods ?? []).map((nome, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            value={nome}
                            maxLength={30}
                            placeholder="Ex: Uber Flash"
                            onChange={(e) => setForm({ ...form, pickupMethods: (form.pickupMethods ?? []).map((n, j) => (j === i ? e.target.value : n)) })}
                            className="flex-1 min-w-0 h-9"
                            data-testid={`settings-pickup-method-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = (form.pickupMethods ?? []).filter((_, j) => j !== i);
                              setForm({ ...form, pickupMethods: next.length > 0 ? next : null });
                            }}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                            aria-label="Remover forma de retirada"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {(form.pickupMethods?.length ?? 0) < 5 && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, pickupMethods: [...(form.pickupMethods ?? []), ""] })}>
                          <Plus className="w-4 h-4 mr-1" /> Adicionar forma
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Aceita entrega</Label>
                <Switch checked={form.allowsDelivery || false} onCheckedChange={(v) => setForm({ ...form, allowsDelivery: v })} />
              </div>
              {form.allowsDelivery && (
                <div className="space-y-5 pl-4 border-l-2 border-primary/20">
                  <div className="space-y-2 pb-4 border-b border-border/40">
                    <Label className="text-sm font-medium">Campos exibidos no link público</Label>
                    <p className="text-xs text-muted-foreground">
                      Configure quais campos do endereço aparecem para o cliente e quais são obrigatórios. Vale tanto quando ele busca pelo Google Maps quanto quando opta por digitar manualmente.
                    </p>
                    <div className="rounded-lg border border-border/60 divide-y divide-border/60 overflow-hidden">
                      {MANUAL_ADDRESS_FIELD_ORDER.map((key) => {
                        const salvo = (form.manualAddressFields && form.manualAddressFields[key]) || MANUAL_ADDRESS_FIELDS_EFFECTIVE[key];
                        // Taxa por bairro: o Bairro é o seletor da taxa no link, então fica ligado e travado (12/10/2026,
                        // pedido do Pedro; o servidor também grava ligado). Desligado, o pedido com endereço manual não fechava.
                        const travado = key === "neighborhood" && form.deliveryFeeType === "neighborhood";
                        const cfg = travado ? { enabled: true, required: salvo.enabled && salvo.required } : salvo;
                        return (
                          <div key={key} className="flex flex-col gap-2 px-3 py-2.5 text-sm">
                            <span className="font-medium">{MANUAL_ADDRESS_FIELD_LABELS[key]}</span>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-1">
                              <label className={`flex items-center gap-1.5 ${travado ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                <Switch
                                  checked={cfg.enabled}
                                  disabled={travado}
                                  data-testid={`settings-campo-${key}-habilitado`}
                                  onCheckedChange={(v) => {
                                    if (travado) return;
                                    const fields = { ...(form.manualAddressFields || MANUAL_ADDRESS_FIELDS_EFFECTIVE) };
                                    fields[key] = { enabled: v, required: v && cfg.required };
                                    setForm({ ...form, manualAddressFields: fields });
                                  }}
                                />
                                <span className="text-xs text-muted-foreground">Habilitado</span>
                              </label>
                              <label className={`flex items-center gap-1.5 ${cfg.enabled ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                                <Switch
                                  disabled={!cfg.enabled}
                                  checked={cfg.enabled && cfg.required}
                                  data-testid={`settings-campo-${key}-obrigatorio`}
                                  onCheckedChange={(v) => {
                                    if (!cfg.enabled) return;
                                    const fields = { ...(form.manualAddressFields || MANUAL_ADDRESS_FIELDS_EFFECTIVE) };
                                    fields[key] = { enabled: cfg.enabled, required: v };
                                    setForm({ ...form, manualAddressFields: fields });
                                  }}
                                />
                                <span className="text-xs text-muted-foreground">Obrigatório</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3 pb-4 border-b border-border/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 flex-1 min-w-0">
                        <Label className="flex items-center gap-2 text-sm font-medium">
                          <span>Endereço Manual</span>
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded font-semibold">
                            <MapPin className="w-3 h-3" /> Maps
                          </span>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Ative para desabilitar a busca de endereço pelo Google Maps. O cliente preencherá os campos manualmente.
                        </p>
                      </div>
                      <Switch
                        checked={!!form.manualAddressMode}
                        onCheckedChange={(v) => {
                          const next: any = { ...form, manualAddressMode: v };
                          if (v && (form.deliveryFeeType === "distance")) {
                            next.deliveryFeeType = "fixed";
                            toast({
                              title: "Cálculo por distância desativado",
                              description: "O cálculo por distância depende do Google Maps. Mudamos para Taxa fixa.",
                            });
                          }
                          if (v && !form.manualAddressFields) {
                            next.manualAddressFields = MANUAL_ADDRESS_FIELDS_EFFECTIVE;
                          }
                          setForm(next);
                        }}
                      />
                    </div>
                    {/* «Receber o link da localização no pedido» (Pedro, 23/10/2026): só a linha «Localização» da mensagem do
                        pedido. Com o Endereço Manual ela some, porque o pedido já chega sem o link. */}
                    {!form.manualAddressMode && (
                      <div className="flex items-start justify-between gap-3" data-testid="settings-location-link">
                        <div className="space-y-1 flex-1 min-w-0">
                          <Label className="text-sm font-medium">Receber o link da localização no pedido</Label>
                          <p className="text-xs text-muted-foreground">
                            A mensagem do pedido traz o link do mapa com o local do cliente.
                          </p>
                        </div>
                        <Switch
                          checked={form.locationLinkInOrder !== false}
                          onCheckedChange={(v) => setForm({ ...form, locationLinkInOrder: v })}
                          data-testid="settings-location-link-switch"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tipo de taxa de entrega</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {([
                        { value: "fixed", label: "Taxa fixa" },
                        { value: "distance", label: "Por distância" },
                        { value: "neighborhood", label: "Por bairro" },
                        { value: "on_request", label: "Calculado após pedido" },
                      ] as const).map((opt) => {
                        // O pedido por distância precisa da LOCALIZAÇÃO da loja, não só do endereço escrito (orders.ts recusa
                        // «Endereço da loja não configurado.»): rua + cidade sem localização passava aqui, e o cliente só
                        // descobria no fim do pedido (16 lojas ativas em 20/10/2026).
                        const lojaLocalizada = !!(form.storeLat && form.storeLng);
                        const enderecoEscrito = !!(form.storeStreet && form.storeCity);
                        const distanceBlockedByManual = !!form.manualAddressMode && opt.value === "distance";
                        const distanceBlockedByAddress = !lojaLocalizada && opt.value === "distance";
                        const isDistanceDisabled = distanceBlockedByManual || distanceBlockedByAddress;
                        const isSelected = porBairroPendente ? opt.value === "neighborhood" : (form.deliveryFeeType || "fixed") === opt.value;
                        const handleClick = () => {
                          if (opt.value === "distance" && !lojaLocalizada) {
                            toast({
                              title: enderecoEscrito ? "Loja sem localização no mapa" : "Endereço da loja não cadastrado",
                              description: enderecoEscrito
                                ? "O cálculo por distância precisa da localização da loja. Em Endereço da Loja, use «Usar minha localização atual» ou busque o endereço. Voltando para Taxa fixa."
                                : "Cadastre o endereço da loja para usar cálculo por distância. Voltando para Taxa fixa.",
                              variant: "destructive",
                            });
                            if ((form.deliveryFeeType || "fixed") === "distance") {
                              setForm({ ...form, deliveryFeeType: "fixed" });
                            }
                            return;
                          }
                          if (isDistanceDisabled) return;
                          if (
                            opt.value === "neighborhood" &&
                            (form.deliveryFeeType || "fixed") !== "neighborhood" &&
                            entregaPorBairroSemSaida({
                              deliveryFeeType: "neighborhood",
                              deliveryNeighborhoods: form.deliveryNeighborhoods,
                              acceptUnlistedNeighborhoods: form.acceptUnlistedNeighborhoods,
                            })
                          ) {
                            setPorBairroPendente(true);
                            return;
                          }
                          setPorBairroPendente(false);
                          // «Por bairro» grava o Bairro ligado (ele é o seletor da taxa no link).
                          const bairro = form.manualAddressFields?.neighborhood;
                          setForm({
                            ...form,
                            deliveryFeeType: opt.value,
                            ...(opt.value === "neighborhood" && bairro && !bairro.enabled
                              ? { manualAddressFields: { ...form.manualAddressFields, neighborhood: { enabled: true, required: false } } }
                              : {}),
                          });
                        };
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={distanceBlockedByManual}
                            onClick={handleClick}
                            title={
                              distanceBlockedByManual
                                ? "Indisponível com Endereço Manual ativo"
                                : distanceBlockedByAddress
                                  ? (enderecoEscrito ? "Falta a localização da loja no mapa" : "Cadastre o endereço da loja primeiro")
                                  : undefined
                            }
                            className={`p-3 rounded-xl border-2 text-center text-sm font-medium transition-all ${
                              isDistanceDisabled
                                ? "border-border/40 bg-muted/30 text-muted-foreground opacity-70 " + (distanceBlockedByManual ? "cursor-not-allowed" : "cursor-pointer")
                                : isSelected
                                  ? "border-accent bg-accent/10 text-foreground cursor-pointer"
                                  : "border-border hover:border-accent/30 cursor-pointer"
                            }`}
                          >
                            <div>{opt.label}</div>
                            {distanceBlockedByManual && (
                              <div className="text-[10px] font-normal mt-1 leading-tight text-red-400">
                                Indisponível<br />desative Endereço Manual
                              </div>
                            )}
                            {!distanceBlockedByManual && distanceBlockedByAddress && (
                              <div className="text-[10px] font-normal mt-1 leading-tight text-red-400">
                                {enderecoEscrito ? <>Falta a localização<br />da loja</> : <>Cadastre o endereço<br />da loja</>}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {!porBairroPendente && (form.deliveryFeeType) === "on_request" && (
                      <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 border border-border/40">
                        O cliente envia o pedido sem taxa calculada. Você define o valor do frete depois e envia para ele pelo novo status "Enviar frete".
                      </p>
                    )}
                  </div>

                  {!porBairroPendente && (form.deliveryFeeType || "fixed") === "fixed" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Taxa de entrega</Label><CurrencyInput value={form.deliveryFee} onChange={(v) => setForm({ ...form, deliveryFee: v })} /></div>
                      <div className="space-y-2"><Label>Grátis acima de</Label><CurrencyInput value={form.freeDeliveryAbove} onChange={(v) => setForm({ ...form, freeDeliveryAbove: v })} placeholder="Deixe vazio para não aplicar" /></div>
                    </div>
                  )}

                  {!porBairroPendente && (form.deliveryFeeType) === "distance" && (() => {
                    // Renderiza sempre a partir da versão normalizada (sem faixas
                    // noDelivery legadas, mins encadeados) pra exibição e edição
                    // ficarem consistentes mesmo com dados antigos no estado.
                    const distanceRanges = chainDistanceRanges(form.deliveryDistanceRanges || []);
                    const lastMax = distanceRanges[distanceRanges.length - 1]?.maxKm;
                    // Loja que já estava com distância sem a localização (antes da trava de 13/05, ou rua + cidade sem
                    // localização): o pedido com entrega é recusado no fim.
                    const semLocalizacao = !(form.storeLat && form.storeLng);
                    return (
                    <div className="space-y-4">
                      {semLocalizacao && (
                        <p className="text-sm rounded-md border border-red-300 bg-red-50 text-red-800 p-3" data-testid="distancia-sem-localizacao">
                          {form.storeStreet && form.storeCity
                            ? "A loja está sem a localização no mapa: hoje o cliente não consegue pedir entrega. Em Endereço da Loja, use «Usar minha localização atual» ou busque o endereço, ou escolha outro tipo de taxa."
                            : "Endereço da loja não cadastrado: hoje o cliente não consegue pedir entrega. Cadastre o endereço da loja ou escolha outro tipo de taxa."}
                        </p>
                      )}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <Label>Faixas de distância</Label>
                          <Button type="button" variant="outline" size="sm" onClick={() => {
                            const last = distanceRanges[distanceRanges.length - 1];
                            const startKm = last ? (last.maxKm ?? 0) : 0;
                            const next = [...distanceRanges, { minKm: startKm, maxKm: startKm + 2, fee: last ? last.fee : 0, noDelivery: false }];
                            setForm({ ...form, deliveryDistanceRanges: chainDistanceRanges(next) });
                          }}><Plus className="w-4 h-4 mr-1" /> Adicionar faixa</Button>
                        </div>
                        {distanceRanges.map((range, idx) => (
                          <div key={idx} className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{idx + 1}ª faixa</span>
                              <button type="button" aria-label="Remover faixa" onClick={() => {
                                const next = distanceRanges.filter((_, i) => i !== idx);
                                setForm({ ...form, deliveryDistanceRanges: chainDistanceRanges(next) });
                              }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-muted-foreground">De</span>
                              {/* minKm é derivado (não editável): a 1ª começa em 0, as
                                  demais onde a anterior terminou. Impede gap entre faixas. */}
                              <div className="flex h-9 w-16 items-center justify-center rounded-md border border-input bg-muted text-sm text-muted-foreground" title="Começa onde a faixa anterior termina">{range.minKm}</div>
                              <span className="text-sm text-muted-foreground">até</span>
                              <NumberInput value={range.maxKm} allowEmpty onChange={(n) => {
                                const next = [...distanceRanges];
                                next[idx] = { ...next[idx], maxKm: n };
                                setForm({ ...form, deliveryDistanceRanges: chainDistanceRanges(next) });
                              }} className="w-20" placeholder="∞" min={range.minKm} step={0.1} />
                              <span className="text-sm text-muted-foreground">km</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Taxa de entrega:</span>
                              <CurrencyInput value={String(range.fee)} onChange={(v) => {
                                const next = [...distanceRanges];
                                next[idx] = { ...next[idx], fee: parseFloat(v || "0") };
                                setForm({ ...form, deliveryDistanceRanges: chainDistanceRanges(next) });
                              }} className="w-32" />
                            </div>
                          </div>
                        ))}
                        {distanceRanges.length > 0 && (
                          <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="distance-beyond-hint">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {lastMax == null
                              ? <span>A última faixa está sem limite (∞): você entrega em qualquer distância. Defina o km final para limitar.</span>
                              : form.distanceBeyondMode === "on_request"
                                ? <span>Acima de <strong>{lastMax} km</strong> o frete é calculado após o pedido.</span>
                                : <span>Acima de <strong>{lastMax} km</strong> sua loja não entrega.</span>}
                          </p>
                        )}
                        {/* Frete a combinar acima da última faixa (Pedro, 14/10/2026): o pedido entra com o frete
                            pendente, como o bairro não cadastrado com "Calcular após o pedido". Só com km final. */}
                        {distanceRanges.length > 0 && lastMax != null && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <Label htmlFor="distance-beyond">Aceitar pedidos acima de {lastMax} km</Label>
                              <Switch
                                id="distance-beyond"
                                checked={form.distanceBeyondMode === "on_request"}
                                onCheckedChange={(v) =>
                                  // As faixas vão como o editor mostra: faixa legada "não entrega" cortaria antes.
                                  setForm({ ...form, distanceBeyondMode: v ? "on_request" : "no_delivery", deliveryDistanceRanges: distanceRanges })
                                }
                                data-testid="distance-beyond-switch"
                              />
                            </div>
                            {form.distanceBeyondMode === "on_request" && (
                              <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 border border-border/40">
                                O pedido chega sem a taxa. Você define o valor depois pelo status "Enviar frete".
                              </p>
                            )}
                          </div>
                        )}
                        {distanceRanges.length === 0 && (
                          <Button type="button" variant="outline" size="sm" onClick={() => {
                            setForm({ ...form, deliveryDistanceRanges: chainDistanceRanges([
                              { minKm: 0, maxKm: 3, fee: 0, noDelivery: false },
                              { minKm: 3, maxKm: 5, fee: 5, noDelivery: false },
                              { minKm: 5, maxKm: 10, fee: 10, noDelivery: false },
                            ]) });
                          }}>
                            <Sparkles className="w-4 h-4 mr-1 text-accent" /> Preencher exemplo
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2"><Label>Grátis acima de</Label><CurrencyInput value={form.freeDeliveryAbove} onChange={(v) => setForm({ ...form, freeDeliveryAbove: v })} placeholder="Deixe vazio para não aplicar" /></div>
                    </div>
                    );
                  })()}

                  {((form.deliveryFeeType) === "neighborhood" || porBairroPendente) && (
                    <div className="space-y-4">
                      {porBairroPendente ? (
                        <p className="text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3" data-testid="por-bairro-pendente">
                          A cobrança por bairro começa a valer quando você cadastrar o primeiro bairro (ou ligar «Aceitar pedidos de bairros não cadastrados»). Até lá, continua valendo a taxa que está hoje.
                        </p>
                      ) : entregaPorBairroSemSaida(form) && (settings && !entregaPorBairroSemSaida(settings as any) ? (
                        <p className="text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3" data-testid="por-bairro-nome-pendente">
                          Digite o nome do bairro. Sem nome, a lista não é salva e continua valendo a que já estava.
                        </p>
                      ) : (
                        <p className="text-sm rounded-md border border-red-300 bg-red-50 text-red-800 p-3" data-testid="por-bairro-sem-saida">
                          Nenhum bairro cadastrado: hoje o cliente não consegue pedir entrega. Cadastre um bairro ou ligue «Aceitar pedidos de bairros não cadastrados».
                        </p>
                      ))}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <Label>Bairros atendidos</Label>
                          <Button type="button" variant="outline" size="sm" onClick={() => {
                            setForm({ ...form, deliveryNeighborhoods: [...(form.deliveryNeighborhoods || []), { name: "", fee: 0, minOrder: 0 }] });
                          }}><Plus className="w-4 h-4 mr-1" /> Adicionar bairro</Button>
                        </div>
                        {/* Pedido mínimo por bairro (03/10/2026): R$ 0,00 = sem
                            mínimo próprio. No celular o nome ocupa a linha e
                            os valores descem com o rótulo em cima. */}
                        {(form.deliveryNeighborhoods || []).length > 0 && (
                          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_7rem_7rem_1rem] gap-2 text-xs text-muted-foreground">
                            <span>Bairro</span><span>Taxa</span><span>Pedido mínimo</span><span />
                          </div>
                        )}
                        {(form.deliveryNeighborhoods || []).map((nb, idx) => (
                          <div key={idx} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2 max-sm:pb-3 max-sm:border-b max-sm:border-border/50 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_1rem] sm:items-center">
                            <Input value={nb.name} onChange={(e) => {
                              const nbs = [...(form.deliveryNeighborhoods || [])];
                              nbs[idx] = { ...nbs[idx], name: e.target.value };
                              setForm({ ...form, deliveryNeighborhoods: nbs });
                            }} placeholder="Nome do bairro" aria-label="Nome do bairro" className="col-span-3 sm:col-span-1" />
                            <div className="space-y-1 min-w-0">
                              <label htmlFor={`nb-fee-${idx}`} className="sm:hidden block text-xs text-muted-foreground">Taxa</label>
                              <CurrencyInput id={`nb-fee-${idx}`} value={String(nb.fee)} onChange={(v) => {
                                const nbs = [...(form.deliveryNeighborhoods || [])];
                                nbs[idx] = { ...nbs[idx], fee: parseFloat(v || "0") };
                                setForm({ ...form, deliveryNeighborhoods: nbs });
                              }} />
                            </div>
                            <div className="space-y-1 min-w-0">
                              <label htmlFor={`nb-min-${idx}`} className="sm:hidden block text-xs text-muted-foreground">Pedido mínimo</label>
                              <CurrencyInput id={`nb-min-${idx}`} value={String(nb.minOrder ?? 0)} onChange={(v) => {
                                const nbs = [...(form.deliveryNeighborhoods || [])];
                                nbs[idx] = { ...nbs[idx], minOrder: parseFloat(v || "0") };
                                setForm({ ...form, deliveryNeighborhoods: nbs });
                              }} />
                            </div>
                            <button type="button" onClick={() => {
                              const nbs = (form.deliveryNeighborhoods || []).filter((_, i) => i !== idx);
                              if (
                                !porBairroPendente &&
                                entregaPorBairroSemSaida({ ...form, deliveryNeighborhoods: nbs }) &&
                                !entregaPorBairroSemSaida(form)
                              ) {
                                toast({
                                  title: "Esse é o último bairro",
                                  description: "Sem nenhum bairro o cliente não consegue pedir entrega. Ligue «Aceitar pedidos de bairros não cadastrados» ou troque o tipo de taxa antes de remover.",
                                  variant: "destructive",
                                });
                                return;
                              }
                              setForm({ ...form, deliveryNeighborhoods: nbs });
                            }} className="h-9 flex items-center justify-center text-red-400 hover:text-red-600" aria-label="Remover bairro"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        {(form.deliveryNeighborhoods || []).length > 0 && (parseFloat(form.minOrderValue || "0") || 0) > 0 && (
                          <p className="text-xs text-muted-foreground">
                            O pedido mínimo geral da loja ({formatCurrency(parseFloat(form.minOrderValue || "0"))}) vale para todos os bairros; o do bairro só conta se for maior.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Aceitar pedidos de bairros não cadastrados</Label>
                        <Switch checked={form.acceptUnlistedNeighborhoods || false} onCheckedChange={(v) => {
                          if (!v && !porBairroPendente && entregaPorBairroSemSaida({ ...form, acceptUnlistedNeighborhoods: false })) {
                            toast({
                              title: "Cadastre um bairro antes",
                              description: "Sem bairro cadastrado e sem aceitar os não cadastrados, o cliente não consegue pedir entrega.",
                              variant: "destructive",
                            });
                            return;
                          }
                          setForm({ ...form, acceptUnlistedNeighborhoods: v });
                        }} />
                      </div>
                      {form.acceptUnlistedNeighborhoods && (
                        <div className="space-y-2">
                          <Label>Taxa para bairros não cadastrados</Label>
                          <Select value={form.unlistedNeighborhoodMode || "fixed"} onValueChange={(v) => setForm({ ...form, unlistedNeighborhoodMode: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Definir um valor</SelectItem>
                              <SelectItem value="on_request">Calcular após o pedido</SelectItem>
                            </SelectContent>
                          </Select>
                          {(form.unlistedNeighborhoodMode || "fixed") === "fixed" ? (
                            <CurrencyInput value={form.unlistedNeighborhoodFee} onChange={(v) => setForm({ ...form, unlistedNeighborhoodFee: v })} />
                          ) : (
                            <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 border border-border/40">
                              O pedido chega sem a taxa. Você define o valor depois pelo status "Enviar frete".
                            </p>
                          )}
                        </div>
                      )}
                      <div className="space-y-2"><Label>Grátis acima de</Label><CurrencyInput value={form.freeDeliveryAbove} onChange={(v) => setForm({ ...form, freeDeliveryAbove: v })} placeholder="Deixe vazio para não aplicar" /></div>
                    </div>
                  )}

                  {/* Cupom × entrega (decisão do Pedro, 22/09/2026). Dois
                      interruptores ao lado do "grátis acima de"; a conta é o
                      couponMath de @workspace/pricing, igual no servidor e no
                      checkout. Frete "calculado após pedido" não tem taxa no
                      checkout, então nada aqui se aplica a ele. */}
                  {(form.deliveryFeeType || "fixed") !== "on_request" && (
                    <div className="space-y-3 pt-3 border-t border-border">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Label className="text-sm font-medium">Cupom não tira o frete grátis</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">O "grátis acima de" olha o valor dos produtos antes do desconto do cupom.</p>
                        </div>
                        <Switch
                          className="mt-0.5 shrink-0"
                          checked={form.freeDeliveryBeforeCoupon === true}
                          onCheckedChange={(v) => setForm({ ...form, freeDeliveryBeforeCoupon: v })}
                          data-testid="settings-free-delivery-before-coupon"
                        />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Label className="text-sm font-medium">Desconto do cupom pode abater a entrega</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Cupom em % desconta a taxa também; cupom em reais, o que sobrar dos produtos abate a taxa.</p>
                        </div>
                        <Switch
                          className="mt-0.5 shrink-0"
                          checked={form.couponCoversDelivery === true}
                          onCheckedChange={(v) => setForm({ ...form, couponCoversDelivery: v })}
                          data-testid="settings-coupon-covers-delivery"
                        />
                      </div>
                    </div>
                  )}

                  {/* «Lançar a taxa de entrega como despesa» (Profissional, Pedro, 23/10/2026). Sem o recurso só dá para
                      desligar: a chave já ligada (plano que venceu) continua podendo sair. */}
                  <div className="flex items-start justify-between gap-3 pt-3 border-t border-border" data-testid="settings-delivery-fee-expense">
                    <div className="min-w-0">
                      <Label className="text-sm font-medium">
                        Lançar a taxa de entrega como despesa {!pagaPlano && <UltraBadge plan="profissional" className="ml-1 align-middle" />}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Entra no Financeiro quando o pedido é marcado como Entregue.</p>
                    </div>
                    <Switch
                      className="mt-0.5 shrink-0"
                      checked={form.deliveryFeeExpenseEnabled === true}
                      disabled={!podeTaxaDespesa && form.deliveryFeeExpenseEnabled !== true}
                      onCheckedChange={(v) => setForm({ ...form, deliveryFeeExpenseEnabled: v })}
                      data-testid="settings-delivery-fee-expense-switch"
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </SectionErrorBoundary>
      )}
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-sugestao" label="Venda Sugestiva">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Sparkles className="w-5 h-5 text-primary" /> Venda Sugestiva</div>
            <div className="space-y-2">
              <Label>Texto da venda sugestiva</Label>
              <Input
                value={(form as any).suggestiveText || ""}
                onChange={(e) => setForm({ ...form, suggestiveText: e.target.value } as any)}
                placeholder="Que tal adicionar também?"
              />
              <p className="text-xs text-muted-foreground">Exibido no carrinho acima dos produtos sugeridos. Deixe vazio para usar o texto padrão.</p>
            </div>
          </Card>
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-promocao" label="Selo de Promoção">
          <PromotionBadgePicker
            form={form}
            setForm={setForm}
          />
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-cupons" label="Cupons de Desconto">
          <CouponsSection
            // Os campos que o cupom pode filtrar são os MESMOS que o cliente vê
            // no link público — daí virem da config já carregada aqui, com o
            // mesmo fallback EFFECTIVE de quem nunca abriu a seção.
            addressFields={form.manualAddressFields || MANUAL_ADDRESS_FIELDS_EFFECTIVE}
            footer={
              <>
                <div className="flex items-start justify-between gap-3 border-t pt-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">Mostrar cupons no link da loja</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Faixa com os cupons ativos no topo da vitrine; o cliente toca e o cupom vai aplicado.
                    </p>
                  </div>
                  <Switch
                    className="mt-0.5 shrink-0"
                    checked={form.showPublicCoupons === true}
                    onCheckedChange={(v) => setForm({ ...form, showPublicCoupons: v })}
                    data-testid="cfg-show-coupons"
                  />
                </div>
                <div className="flex items-start justify-between gap-3 border-t pt-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">Cupons cumulativos</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cliente pode combinar mais de um cupom no mesmo pedido.
                    </p>
                  </div>
                  <Switch
                    className="mt-0.5 shrink-0"
                    checked={form.stackableCoupons === true}
                    onCheckedChange={(v) => setForm({ ...form, stackableCoupons: v })}
                    data-testid="cfg-stackable-coupons"
                  />
                </div>
              </>
            }
          />
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-produtos" label="Produtos">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Package className="w-5 h-5 text-primary" /> Produtos</div>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Esconder produtos pausados do link público</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Produto pausado some do cardápio em vez de aparecer como "Indisponível".
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.hidePausedProducts}
                onCheckedChange={(v) => setForm({ ...form, hidePausedProducts: v })}
              />
            </div>
            {/* Pedro, 30/10/2026: desligado por padrão, o produto indisponível no link continua à venda no balcão (o ingresso
                que só a casa vende). Mora no pdvSettings: mudar aqui salva a seção do PDV. */}
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Esconder produtos indisponíveis no PDV</Label>
                <p className="text-xs text-muted-foreground mt-1">Também no «+ Pedido» de Comandas e Mesas.</p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!(form as any).pdvSettings?.esconderIndisponiveis}
                onCheckedChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), esconderIndisponiveis: v } } as any)}
                data-testid="pdv-settings-esconder-indisponiveis"
              />
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Esconder produtos esgotados do link público</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  O produto com estoque zerado some do link e volta sozinho quando o estoque voltar.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.hideOutOfStockProducts}
                onCheckedChange={(v) => setForm({ ...form, hideOutOfStockProducts: v })}
                data-testid="settings-hide-out-of-stock"
              />
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Esconder opções esgotadas do link público</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Sabor, adicional ou ingrediente esgotado ou pausado some do produto em vez de aparecer como "Esgotado".
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.hideUnavailableOptions}
                onCheckedChange={(v) => setForm({ ...form, hideUnavailableOptions: v })}
                data-testid="settings-hide-unavailable-options"
              />
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Imprimir o número do pedido no cupom</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Desligue para o número não sair no cupom nem nas mensagens. Na página de Pedidos ele continua.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.dailyOrderNumbering !== false}
                onCheckedChange={(v) => setForm({ ...form, dailyOrderNumbering: v })}
              />
            </div>
            <div className="space-y-2 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Estilo do número do pedido</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Como o pedido é identificado no painel, no cupom e nas mensagens. <strong>Número do dia</strong> reinicia todo dia (#0001, #0002…); <strong>Código do pedido</strong> é o estilo antigo, um código fixo de letras e números (ex: #A1B2). Pode trocar quando quiser — só muda a exibição.
                </p>
              </div>
              <select
                className="w-full sm:w-auto border rounded-md p-2 text-sm bg-background h-10"
                value={form.orderCodeStyle === "codigo" ? "codigo" : "numero"}
                onChange={(e) => setForm({ ...form, orderCodeStyle: e.target.value })}
                data-testid="settings-order-code-style"
              >
                <option value="numero">Número do dia (#0001)</option>
                <option value="codigo">Código do pedido (#A1B2)</option>
              </select>
              {(form.orderCodeStyle ?? "numero") !== "codigo" && (
                <div className="space-y-1 pt-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="daily-order-start" className="text-sm font-normal">Todo dia começar no número</Label>
                    <Input
                      id="daily-order-start"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={9999}
                      className="w-24 h-9"
                      value={dailyStartText ?? String(form.dailyOrderStart ?? 1)}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setDailyStartText(digits);
                        // Campo vazio não salva: volta pro último valor ao sair.
                        if (digits !== "") setForm({ ...form, dailyOrderStart: parseInt(digits, 10) });
                      }}
                      onBlur={() => setDailyStartText(null)}
                      data-testid="settings-daily-order-start"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A contagem de cada dia começa neste número — vale a partir de amanhã.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Data única no carrinho</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  O cliente escolhe uma data (e o horário ou o turno, se algum produto pedir) para o pedido inteiro, no fim do pedido, em vez de um por produto por encomenda.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.orderSingleDate}
                onCheckedChange={(v) => setForm({ ...form, orderSingleDate: v })}
                data-testid="settings-order-single-date"
              />
            </div>
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm font-medium">Limitar encomendas por dia</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Quando o dia chega ao limite, ele fica indisponível para os próximos clientes, em qualquer produto por encomenda da loja.
                  </p>
                </div>
                <Switch
                  className="mt-0.5 shrink-0"
                  checked={diaLimite != null}
                  onCheckedChange={(v) => { setDiaLimiteText(null); setForm({ ...form, orderDayLimit: v ? 1 : null }); }}
                  data-testid="settings-order-day-limit-toggle"
                />
              </div>
              {diaLimite != null && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="order-day-limit" className="text-sm font-normal">Até</Label>
                  <Input
                    id="order-day-limit"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={999}
                    className="w-20 h-9"
                    value={diaLimiteText ?? String(diaLimite)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
                      setDiaLimiteText(digits);
                      // Vazio ou zero não salva: volta pro último valor ao sair.
                      const n = parseInt(digits, 10);
                      if (n >= 1) setForm({ ...form, orderDayLimit: n });
                    }}
                    onBlur={() => setDiaLimiteText(null)}
                    data-testid="settings-order-day-limit"
                  />
                  <span className="text-sm">{diaLimite === 1 ? "pedido por dia" : "pedidos por dia"}</span>
                </div>
              )}
            </div>
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className={`text-sm font-medium ${diaLimite === 1 ? "text-muted-foreground" : ""}`}>Uma encomenda por horário</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {diaLimite === 1
                      ? "Sem efeito com o limite de 1 pedido por dia: o dia inteiro já fica para um pedido."
                      : "O horário (ou o turno) escolhido por um cliente fica indisponível para os próximos, em qualquer produto por encomenda da loja."}
                  </p>
                </div>
                <Switch
                  className="mt-0.5 shrink-0"
                  checked={!!form.orderSlotExclusive}
                  disabled={diaLimite === 1}
                  onCheckedChange={(v) => setForm({ ...form, orderSlotExclusive: v })}
                  data-testid="settings-order-slot-exclusive"
                />
              </div>
              {!!form.orderSlotExclusive && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-normal">Intervalo entre horários</Label>
                  <select
                    className="border rounded-md p-2 text-sm bg-background h-10"
                    value={String(form.orderSlotIntervalMinutes ?? 30)}
                    onChange={(e) => setForm({ ...form, orderSlotIntervalMinutes: parseInt(e.target.value, 10) })}
                    data-testid="settings-order-slot-interval"
                  >
                    {[15, 30, 45, 60, 90, 120].map((m) => (
                      <option key={m} value={m}>{m >= 60 ? `${m / 60}h${m % 60 ? String(m % 60).padStart(2, "0") : ""}` : `${m} min`}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {settings?.hasShiftProducts && (
            <div className="space-y-3 border-t pt-4 sm:space-y-2" data-testid="settings-turnos">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Horário dos turnos</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Vale para os produtos por encomenda em que o cliente escolhe o turno. Sem fim, a Noite fica "a partir de".
                </p>
              </div>
              {TURNOS.map((t) => {
                // Sem horário próprio salvo, mostra o que sai do horário de funcionamento (o efetivo); com, o que a loja
                // digitou (o servidor confere ao salvar). Mexer em qualquer coisa salva o horário próprio inteiro.
                const efetivo = horarioDosTurnos(form.orderShiftHours ?? null, form.businessHours);
                const gravado = form.orderShiftHours ?? null;
                const atual = (x: (typeof TURNOS)[number]) => {
                  const g = gravado?.[x];
                  if (gravado && g === null) return { ...efetivo[x], ligado: false };
                  if (g && g.start) return { start: g.start, end: g.end ?? null, ligado: g.ligado !== false };
                  return efetivo[x];
                };
                const muda = (patch: Partial<{ start: string; end: string | null; ligado: boolean }>) =>
                  setForm({
                    ...form,
                    orderShiftHours: {
                      ...Object.fromEntries(TURNOS.map((x) => [x, atual(x)])),
                      [t]: { ...atual(t), ...patch },
                    },
                  });
                const este = atual(t);
                const ultimoLigado = este.ligado && TURNOS.filter((x) => atual(x).ligado).length === 1;
                // Celular: nome e Switch numa linha (o Switch na borda, como os outros da seção) e os horários embaixo —
                // tudo numa linha só passava da largura do cartão. Do sm em diante, uma linha só.
                return (
                  <div key={t} className="grid grid-cols-[1fr_auto] items-center gap-x-2 gap-y-1.5 sm:flex sm:gap-2">
                    <span className={`text-sm sm:w-16 sm:shrink-0 ${este.ligado ? "" : "text-muted-foreground"}`}>{NOME_DO_TURNO[t]}</span>
                    <Switch checked={este.ligado} disabled={ultimoLigado} onCheckedChange={(v) => muda({ ligado: v })} aria-label={`${NOME_DO_TURNO[t]} ligada`} data-testid={`settings-turno-${t}-ligado`} className="shrink-0 justify-self-end sm:order-last sm:ml-1" />
                    <div className="col-span-2 flex min-w-0 items-center gap-2">
                      <Input type="time" value={este.start} disabled={!este.ligado} onChange={(e) => e.target.value && muda({ start: e.target.value })} className="w-28 min-w-0 h-9 text-sm" aria-label={`Começo da ${NOME_DO_TURNO[t]}`} data-testid={`settings-turno-${t}-inicio`} />
                      <span className="text-xs text-muted-foreground">até</span>
                      <Input type="time" value={este.end ?? ""} disabled={!este.ligado} onChange={(e) => muda({ end: e.target.value || null })} className="w-28 min-w-0 h-9 text-sm" aria-label={`Fim da ${NOME_DO_TURNO[t]}`} data-testid={`settings-turno-${t}-fim`} />
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Lançar o custo do produto como despesa</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cada venda vira uma despesa com o custo do produto — desligue se você já lança as compras de material.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.costExpenseEnabled !== false}
                onCheckedChange={(v) => setForm({ ...form, costExpenseEnabled: v })}
                data-testid="settings-cost-expense-mode"
              />
            </div>

            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Mostrar títulos dos adicionais no pedido</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Desligue para sair só a lista de adicionais, sem os títulos dos grupos.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.showGroupTitles !== false}
                onCheckedChange={(v) => setForm({ ...form, showGroupTitles: v })}
              />
            </div>

            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Mostrar a data e a hora do pedido na mensagem</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  A mensagem de Novo Pedido no WhatsApp sai com «Pedido feito em: 23/10/2026 às 21:27». Ajuda a ver quando o cliente reenviou um pedido antigo.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.orderMessageShowDate === true}
                onCheckedChange={(v) => setForm({ ...form, orderMessageShowDate: v })}
                data-testid="settings-order-message-show-date"
              />
            </div>

            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Mostrar quantidade restante dos adicionais no link</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Desligue para o cliente não ver "Restam 5" nos adicionais.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.showOptionStock !== false}
                onCheckedChange={(v) => setForm({ ...form, showOptionStock: v })}
                data-testid="settings-show-option-stock"
              />
            </div>

            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Categoria "Mais vendidos" no link {mostrarSelo && <UltraBadge className="ml-1 align-middle" />}</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Pílula 🔥 na frente das categorias, com selo de posição.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={form.showBestSellers === true}
                disabled={!podeFeature("best_sellers")}
                onCheckedChange={(v) => setForm({ ...form, showBestSellers: v })}
                data-testid="cfg-show-best"
              />
            </div>
            {form.showBestSellers === true && (
              <FeaturedProductsPicker
                value={form.featuredProducts ?? null}
                onChange={(ids) => setForm({ ...form, featuredProducts: ids })}
              />
            )}
          </Card>
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      <SectionErrorBoundary anchorId="cfg-clientes" label="Clientes">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold flex-wrap">
            <Users className="w-5 h-5 text-primary" /> Clientes
            {mostrarSelo && <UltraBadge />}
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Pedir data de nascimento</Label>
              <p className="text-xs text-muted-foreground mt-1">
                O cliente informa no checkout; aparece no pedido e na página de Clientes.
              </p>
            </div>
            <Switch
              className="mt-0.5 shrink-0"
              checked={!!form.askBirthDate}
              disabled={!podeFeature("birthday")}
              onCheckedChange={(v) => setForm({ ...form, askBirthDate: v, ...(v ? {} : { birthDateRequired: false }) })}
            />
          </div>
          {form.askBirthDate && (
            <div className="flex items-start justify-between gap-3 border-t pt-4">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Obrigatório</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  O cliente não conclui o cadastro/pedido sem informar a data.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.birthDateRequired}
                onCheckedChange={(v) => setForm({ ...form, birthDateRequired: v })}
              />
            </div>
          )}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Lembretes de aniversário</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  O Dashboard mostra os aniversariantes do dia e dos próximos dias, com nome e WhatsApp.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.birthdayReminders}
                disabled={!podeFeature("birthday")}
                onCheckedChange={(v) => setForm({ ...form, birthdayReminders: v })}
                data-testid="settings-birthday-reminders"
              />
            </div>
            {!!form.birthdayReminders && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                <Label className="text-sm font-normal">Avisar com</Label>
                <select
                  className="border rounded-md p-2 text-sm bg-background h-10"
                  value={String(form.birthdayAdvanceDays ?? 3)}
                  onChange={(e) => setForm({ ...form, birthdayAdvanceDays: parseInt(e.target.value, 10) })}
                  data-testid="settings-birthday-advance"
                >
                  <option value="0">só no dia</option>
                  <option value="1">1 dia de antecedência</option>
                  <option value="3">3 dias de antecedência</option>
                  <option value="7">7 dias de antecedência</option>
                </select>
                <Label className="text-sm font-normal">e lembrar até</Label>
                <select
                  className="border rounded-md p-2 text-sm bg-background h-10"
                  value={String(form.birthdayPastDays ?? 2)}
                  onChange={(e) => setForm({ ...form, birthdayPastDays: parseInt(e.target.value, 10) })}
                  data-testid="settings-birthday-past"
                >
                  <option value="0">só até o dia</option>
                  <option value="1">1 dia depois</option>
                  <option value="2">2 dias depois</option>
                  <option value="3">3 dias depois</option>
                  <option value="7">7 dias depois</option>
                </select>
              </div>
            )}
            {!!form.birthdayReminders && (
              <div className="space-y-1 pt-1">
                <Label className="text-sm font-medium">Mensagem do WhatsApp</Label>
                <textarea
                  value={form.birthdayWhatsappMessage ?? "Feliz aniversário, {nome}! 🎉"}
                  onChange={(e) => setForm({ ...form, birthdayWhatsappMessage: e.target.value })}
                  maxLength={400}
                  rows={3}
                  className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
                  data-testid="settings-birthday-whatsapp-message"
                />
                <p className="text-xs text-muted-foreground">Abre preenchida no botão Parabenizar. {"{nome}"} vira o nome do cliente.</p>
              </div>
            )}
          </div>
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Mensagem de aniversário na impressão</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Sai "Feliz aniversário, {"{nome}"}!" no fim do cupom de quem pedir no dia.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.birthdayPrintEnabled}
                disabled={!podeFeature("birthday")}
                onCheckedChange={(v) => setForm({ ...form, birthdayPrintEnabled: v })}
                data-testid="settings-birthday-print"
              />
            </div>
          </div>
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label className="text-sm font-medium">Perguntar se é para presente</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  O cliente responde no checkout; o nome de quem recebe aparece no pedido e no cupom.
                </p>
              </div>
              <Switch
                className="mt-0.5 shrink-0"
                checked={!!form.askGiftRecipient}
                disabled={!podeFeature("gift_recipient")}
                onCheckedChange={(v) => setForm({ ...form, askGiftRecipient: v, ...(v ? {} : { giftRecipientRequired: false }) })}
                data-testid="settings-gift-ask"
              />
            </div>
            {!!form.askGiftRecipient && (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm font-medium">Obrigatório</Label>
                  <p className="text-xs text-muted-foreground mt-1">O cliente não conclui o pedido sem responder.</p>
                </div>
                <Switch
                  className="mt-0.5 shrink-0"
                  checked={!!form.giftRecipientRequired}
                  disabled={!podeFeature("gift_recipient")}
                  onCheckedChange={(v) => setForm({ ...form, giftRecipientRequired: v })}
                  data-testid="settings-gift-required"
                />
              </div>
            )}
          </div>
        </Card>
      </SectionErrorBoundary>
      {showProducts && (
        <>
          <hr className="border-border/40" />
          <SectionErrorBoundary anchorId="cfg-pontos" label="Programa de pontos">
            <LoyaltySection
              enabled={!!form.loyaltyEnabled}
              config={form.loyaltyConfig}
              podeUsar={podeFeature("loyalty")}
              mostrarSelo={mostrarSelo}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            />
          </SectionErrorBoundary>
        </>
      )}
      <hr className="border-border/40" />
      <SectionErrorBoundary anchorId="cfg-notificacoes" label="Notificações Push">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><Bell className="w-5 h-5 text-primary" /> Notificações Push</div>
          <p className="text-sm text-muted-foreground">
            Receba notificações em tempo real quando um cliente fizer um pedido ou agendar um serviço.
          </p>
          {push.state === "unsupported" && tipoIphone && tipoIphone !== "icone" ? (
            <AvisoDoIphone tipo={tipoIphone} />
          ) : push.state === "unsupported" ? (
            <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground flex items-center gap-2">
              <BellOff className="w-4 h-4" />
              Seu navegador não suporta notificações push.
            </div>
          ) : push.state === "denied" ? (
            <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
              <BellOff className="w-4 h-4" />
              Notificações bloqueadas. Libere nas configurações do seu navegador.
            </div>
          ) : push.isSubscribed ? (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notificações ativadas neste dispositivo.
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  disabled={testingPush}
                  onClick={async () => {
                    setTestingPush(true);
                    try {
                      const res = await customFetch<{ sent?: number }>("/api/push/test", { method: "POST" });
                      let sent = res?.sent ?? 0;
                      // sent=0 = servidor sem inscrição pra este dispositivo (o selo
                      // verde mente quando o backend apagou a linha por 410). Re-registra
                      // e tenta de novo antes de reportar — nunca dizer "enviada" sem envio.
                      if (sent === 0) {
                        const ok = await push.subscribe();
                        if (ok) {
                          const retry = await customFetch<{ sent?: number }>("/api/push/test", { method: "POST" });
                          sent = retry?.sent ?? 0;
                        }
                      }
                      if (sent > 0) {
                        toast({ title: `Notificação enviada para ${sent} dispositivo${sent > 1 ? "s" : ""}.` });
                      } else {
                        toast({
                          title: "Nenhum dispositivo recebeu",
                          description: "Reative as notificações neste dispositivo e tente de novo.",
                          variant: "destructive",
                        });
                      }
                    } catch {
                      toast({ title: "Erro", description: "Falha ao enviar teste.", variant: "destructive" });
                    } finally {
                      setTestingPush(false);
                    }
                  }}
                >
                  {testingPush ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
                  Testar
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive cursor-pointer" onClick={() => push.unsubscribe()}>
                  <BellOff className="w-4 h-4 mr-1" /> Desativar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              className="cursor-pointer"
              onClick={async () => {
                const ok = await push.subscribe();
                if (ok) toast({ title: "Notificações ativadas!" });
                else if (push.state !== "denied") toast({ title: "Erro", description: "Não foi possível ativar as notificações.", variant: "destructive" });
              }}
            >
              <Bell className="w-4 h-4 mr-2" /> Ativar Notificações
            </Button>
          )}
        </Card>
      </SectionErrorBoundary>
      <SectionErrorBoundary anchorId="cfg-sons" label="Som de novo pedido">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><Volume2 className="w-5 h-5 text-primary" /> Som de novo pedido</div>
          <p className="text-sm text-muted-foreground">
            Toca um alarme no painel quando entra pedido novo, com o painel aberto.
          </p>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="order-sound" className="cursor-pointer">Ao receber pedido</Label>
            <Select
              value={soundMode}
              onValueChange={(v) => {
                const mode = v as OrderSoundMode;
                setSoundMode(mode);
                setOrderSoundMode(mode);
              }}
            >
              <SelectTrigger id="order-sound" className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Desativado</SelectItem>
                <SelectItem value="once">Tocar 1 vez</SelectItem>
                <SelectItem value="loop">Parar com clique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {soundMode !== "off" && (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => previewOrderSound()}
            >
              <Volume2 className="w-4 h-4 mr-1" /> Testar som
            </Button>
          )}
        </Card>
      </SectionErrorBoundary>
      {(() => {
        const printerLocked = false;
        // Largura do papel agora é uma config DA LOJA (pdvSettings.paperWidth):
        // vale pra impressão no navegador E pro app de impressão (Agente, que lê
        // pdvSettings no servidor). printer.prefs é só fallback legado de exibição.
        const pdvWidth = (form as any).pdvSettings?.paperWidth;
        const selectedPaperWidth: "58mm" | "80mm" =
          pdvWidth === "58mm" || pdvWidth === "80mm" ? pdvWidth : printer.prefs.paperWidth;
        const setPaperWidth = (w: "58mm" | "80mm") => {
          printer.updatePrefs({ paperWidth: w });
          setForm((prev) => ({ ...prev, pdvSettings: { ...((prev as any).pdvSettings || {}), paperWidth: w } } as any));
        };
        // "Imprimir ao receber pedido" também é config DA LOJA: quem imprime pelo
        // app é o servidor que enfileira, e ele não enxerga o localStorage. Sem o
        // espelho, desligar o toggle não parava o cupom no app (Top_delícia 13/08).
        const pdvAutoPrintNewOrder = (form as any).pdvSettings?.autoPrintNewOrder;
        const autoPrintNewOrder: boolean =
          typeof pdvAutoPrintNewOrder === "boolean" ? pdvAutoPrintNewOrder : printer.prefs.autoPrintNewOrder;
        const setAutoPrintNewOrder = (v: boolean) => {
          printer.updatePrefs({ autoPrintNewOrder: v });
          setForm((prev) => ({ ...prev, pdvSettings: { ...((prev as any).pdvSettings || {}), autoPrintNewOrder: v } } as any));
        };
        // Idem pro gatilho de status: quem enfileira agora é o PUT /:id/status,
        // então o valor precisa estar no servidor — senão mudar o status por
        // outro aparelho não imprime.
        const pdvAutoPrintStatus = (form as any).pdvSettings?.autoPrintStatusChange;
        const autoPrintStatusChange: boolean =
          typeof pdvAutoPrintStatus === "boolean" ? pdvAutoPrintStatus : printer.prefs.autoPrintStatusChange;
        const pdvAutoPrintStatusList = (form as any).pdvSettings?.autoPrintStatusList;
        const autoPrintStatusList: string[] = Array.isArray(pdvAutoPrintStatusList)
          ? pdvAutoPrintStatusList
          : (printer.prefs.autoPrintStatusList || []);
        const setAutoPrintStatus = (change: boolean, list: string[]) => {
          printer.updatePrefs({ autoPrintStatusChange: change, autoPrintStatusList: list });
          setForm((prev) => ({
            ...prev,
            pdvSettings: {
              ...((prev as any).pdvSettings || {}),
              autoPrintStatusChange: change,
              autoPrintStatusList: list,
            },
          } as any));
        };
        return (
        <>
        {/* Card — Impressora Térmica (conexão direta USB / app de impressão) */}
        <SectionErrorBoundary anchorId="cfg-impressora" label="Impressora Térmica">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Printer className="w-5 h-5 text-primary" /> Impressora Térmica</div>
            {!printer.supported ? (
              <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground space-y-2">
                <p>A conexão direta com a impressora USB não funciona neste navegador.</p>
                <p>No iPhone/iPad o navegador não permite conexão USB direta. Pra imprimir, use o <strong>app de impressão</strong> abaixo (Windows ou Android), ou abra o painel no <strong>Chrome no Android/PC</strong> pra conectar a impressora via USB.</p>
              </div>
            ) : (
            <>
            {appWindowsAberto && !printer.connected ? (
              <p className="text-sm text-muted-foreground">
                A loja já imprime pelo aplicativo de impressão do Windows. Não precisa conectar a impressora por aqui.
              </p>
            ) : (
            <>
            <p className="text-sm text-muted-foreground">
              Conecte uma impressora térmica via USB para imprimir cupons de pedidos automaticamente.
            </p>

            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              Para imprimir, seu sistema deve ficar aberto.
            </p>

            {/* Em mobile (≤375px) o par [chip status] + [botão "Conectar Impressora"]
                não cabia lado a lado — o botão vazava do card. Empilha em <sm,
                volta a ficar lado a lado em sm:+. O botão fica w-full no mobile
                pra atingir 44px de target em qualquer comprimento de label. */}
            <div className={`flex flex-col sm:flex-row sm:items-center gap-3 ${printerLocked ? 'opacity-60' : ''}`}>
              <div className={`self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${printer.connected ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                <div className={`w-2 h-2 rounded-full ${printer.connected ? 'bg-green-500' : 'bg-gray-400'}`} />
                {printer.connected ? "Conectada" : "Desconectada"}
              </div>

              {printer.connected ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    disabled={testingPrint || printerLocked}
                    onClick={async () => {
                      setTestingPrint(true);
                      try {
                        const storeName = form.name || "Minha Loja";
                        const receipt = buildReceipt(
                          ESC_POS.INIT,
                          ESC_POS.ALIGN_CENTER,
                          ESC_POS.BOLD_ON, ESC_POS.DOUBLE_ON,
                          line(storeName),
                          ESC_POS.DOUBLE_OFF, ESC_POS.BOLD_OFF,
                          ESC_POS.LF,
                          separator(printer.prefs.paperWidth),
                          ESC_POS.ALIGN_CENTER,
                          line("*** TESTE DE IMPRESSAO ***"),
                          ESC_POS.LF,
                          line("Impressora funcionando!"),
                          line(`Largura: ${printer.prefs.paperWidth}`),
                          ESC_POS.LF,
                          separator(printer.prefs.paperWidth),
                          line("Obrigado pela preferencia!"),
                          ESC_POS.LF,
                          ESC_POS.FEED_5,
                          ESC_POS.PARTIAL_CUT,
                        );
                        // O printData devolve texto ("printed" | "failed" sem pedido), e "failed" também é verdadeiro.
                        const ok = (await printer.printData(receipt)) === "printed";
                        toast({ title: ok ? "Teste impresso!" : "Falha ao imprimir" , variant: ok ? "default" : "destructive" });
                      } catch {
                        toast({ title: "Erro ao imprimir", variant: "destructive" });
                      }
                      setTestingPrint(false);
                    }}
                  >
                    {testingPrint ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
                    Imprimir teste
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive cursor-pointer" disabled={printerLocked} onClick={() => printer.disconnect()}>
                    Desconectar
                  </Button>
                </div>
              ) : (
                <Button
                  className="cursor-pointer w-full sm:w-auto"
                  size="sm"
                  disabled={printer.connecting || printerLocked}
                  onClick={async () => {
                    if (printer.queue.length > 0) {
                      setShowQueueDialog(true);
                      return;
                    }
                    const result = await printer.connect();
                    if (result.ok) {
                      toast({ title: "Impressora conectada!" });
                    } else if (result.error && /Acesso negado/i.test(result.error)) {
                      setShowPrintAgentSuggestion(true);
                    } else {
                      toast({ title: "Não foi possível conectar", description: result.error, variant: "destructive" });
                    }
                  }}
                >
                  {printer.connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
                  Conectar Impressora
                </Button>
              )}
            </div>
            </>
            )}

            {printer.queue.length > 0 && (
              <div className={`flex flex-col gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 sm:flex-row sm:items-center sm:justify-between ${printerLocked ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    {printer.queue.length} {printer.queue.length === 1 ? "pedido aguardando" : "pedidos aguardando"} impressão
                  </p>
                </div>
                <div className="flex gap-2 sm:shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none cursor-pointer text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/30"
                    disabled={printer.processingQueue || !printer.connected || printerLocked}
                    onClick={() => printer.flushQueue()}
                  >
                    {printer.processingQueue ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />}
                    Imprimir fila
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 sm:flex-none cursor-pointer text-amber-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                    disabled={printerLocked}
                    onClick={() => printer.clearQueue()}
                  >
                    <X className="w-3.5 h-3.5 mr-1" />
                    Limpar
                  </Button>
                </div>
              </div>
            )}
            </>
            )}
            <PrintAgentSection />
            {(() => {
              const devices = agentDevices;
              const multiDevice = !!(form as any).pdvSettings?.multiDevicePrint;
              // "Ativo" espelha a janela do fan-out no servidor (48h).
              const activeCount = devices.filter(
                (d) => !!d.lastSeenAt && Date.now() - new Date(d.lastSeenAt).getTime() < 48 * 60 * 60 * 1000,
              ).length;
              return (
                <div className="space-y-3 pt-3 border-t border-border">
                  {devices.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium">Aparelhos de impressão conectados</p>
                      {devices.map((d) => (
                        <div key={d.deviceId} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="truncate">
                            {d.hostname || "Aparelho"}
                            {d.os ? ` · ${d.os}` : ""}
                          </span>
                          <span className="shrink-0">{d.lastSeenAt ? `visto ${lastSeenLabel(d.lastSeenAt)}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Imprimir em todos os aparelhos</p>
                      <p className="text-xs text-muted-foreground">
                        Ligue se você usa o app de impressão em mais de um computador ou celular
                        (ex.: balcão e cozinha) e quer que <strong>todos</strong> imprimam cada
                        pedido. Desligado, apenas um aparelho imprime cada pedido.
                      </p>
                    </div>
                    <Switch
                      checked={multiDevice}
                      onCheckedChange={(v) =>
                        setForm((prev) => ({
                          ...prev,
                          pdvSettings: { ...((prev as any).pdvSettings || {}), multiDevicePrint: v },
                        }) as any)
                      }
                    />
                  </div>
                  {activeCount > 1 && !multiDevice && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-300">
                        Detectamos {activeCount} aparelhos de impressão nesta loja. Com a
                        configuração atual, apenas um imprime cada pedido — e eles podem disputar
                        entre si. Desative o app no aparelho que você não usa, ou ligue
                        {" "}<strong>Imprimir em todos os aparelhos</strong>.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Letra maior no cupom</p>
                <p className="text-xs text-muted-foreground">O texto do cupom sai mais alto, na mesma largura, para ler melhor.</p>
              </div>
              <Switch
                checked={form.receiptFont === "alta"}
                onCheckedChange={(v) => setForm((prev) => ({ ...prev, receiptFont: v ? "alta" : "normal" }))}
                data-testid="settings-receipt-font"
              />
            </div>
            {(() => {
              // «Mensagem no rodapé do cupom» (Pedro, 23/10/2026): o aviso aponta, enquanto ela digita, o que não sai na
              // impressora (a regra do servidor, @workspace/escpos).
              // A mensagem TROCA o «Obrigado pela preferência!» (Pedro, 23/10/2026): a caixa abre com o texto que sai hoje
              // e ela edita. Vazio ou o próprio Obrigado grava null, e ao sair da caixa ela volta a mostrar o Obrigado.
              const salvo = form.receiptFooterMessage || RODAPE_PADRAO;
              const texto = rascunhoRodape ?? salvo;
              const aviso = erroDoRodape(texto);
              const guardadoSemPlano = !podeRodape && !!rodapeParaGravar(form.receiptFooterMessage);
              return (
                <div className="space-y-1.5 border-t border-border pt-4" data-testid="settings-receipt-footer">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Mensagem no rodapé do cupom</p>
                      <p className="text-xs text-muted-foreground">A última linha do cupom. Troque o "Obrigado pela preferência!" pelo seu recado.</p>
                    </div>
                    {!pagaPlano && <UltraBadge plan="profissional" className="shrink-0" />}
                  </div>
                  <textarea
                    value={texto}
                    onFocus={() => { if (rascunhoRodape === null) setRascunhoRodape(salvo); }}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRascunhoRodape(v);
                      if (!erroDoRodape(v)) setForm((prev) => ({ ...prev, receiptFooterMessage: rodapeParaGravar(v) }));
                    }}
                    onBlur={() => { if (!erroDoRodape(rascunhoRodape ?? "")) setRascunhoRodape(null); }}
                    readOnly={!podeRodape}
                    rows={3}
                    placeholder={RODAPE_PADRAO}
                    aria-invalid={!!aviso}
                    className={`w-full rounded-md border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content] read-only:cursor-not-allowed read-only:opacity-60 ${aviso ? "border-red-500" : "border-input"}`}
                    data-testid="settings-receipt-footer-message"
                  />
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <p className={aviso ? "text-red-600" : "text-muted-foreground"} data-testid="settings-receipt-footer-hint">
                      {aviso ?? (guardadoSemPlano ? 'No plano Grátis sai o "Obrigado pela preferência!". A sua mensagem volta quando você assinar.' : "")}
                    </p>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{Array.from(normalizarRodape(texto)).length}/{RODAPE_MAX_CARACTERES}</span>
                  </div>
                </div>
              );
            })()}
          </Card>
        </SectionErrorBoundary>

        {/* Card — Largura do papel (independente: sempre visível, vale pro agente também) */}
        <SectionErrorBoundary anchorId="cfg-papel" label="Largura do papel">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Printer className="w-5 h-5 text-primary" /> Largura do papel</div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">Conforme sua impressora — vale pra impressão no navegador e no app (Agente)</p>
                </div>
                <div className={`flex border rounded-lg overflow-hidden shrink-0 ${printerLocked ? 'opacity-60' : ''}`}>
                  <button
                    type="button"
                    disabled={printerLocked}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${printerLocked ? 'cursor-not-allowed' : 'cursor-pointer'} ${selectedPaperWidth === "58mm" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                    onClick={() => setPaperWidth("58mm")}
                  >58mm</button>
                  <button
                    type="button"
                    disabled={printerLocked}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${printerLocked ? 'cursor-not-allowed' : 'cursor-pointer'} ${selectedPaperWidth === "80mm" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                    onClick={() => setPaperWidth("80mm")}
                  >80mm</button>
                </div>
              </div>
          </Card>
        </SectionErrorBoundary>

        {/* Card — Impressão automática (independente, sempre visível) */}
        <SectionErrorBoundary anchorId="cfg-impressao-auto" label="Impressão automática">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold"><Printer className="w-5 h-5 text-primary" /> Impressão automática</div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Imprimir ao receber pedido</p>
                  <p className="text-xs text-muted-foreground">Imprime automaticamente quando um novo pedido chegar</p>
                </div>
                <Switch disabled={printerLocked} checked={autoPrintNewOrder} onCheckedChange={setAutoPrintNewOrder} />
              </div>

              {/* Perto do «Imprimir ao receber pedido» (Pedro, 01/11/2026, A-0065): o pedido de conta do cliente, na mesa ou
                  na comanda. Só aparece com o pedido na mesa ligado; a escolha do papel é a mesma do cartão da mesa. */}
              {!!form.tableOrdersEnabled && (
                <div className="space-y-2 border-t pt-3" data-testid="settings-conta-pedida">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Imprimir quando o cliente pedir a conta</p>
                      <p className="text-xs text-muted-foreground">
                        Quando o cliente toca em «Pedir minha conta» no celular ({form.tableQrPerClient === true ? "comanda" : "mesa"}).
                      </p>
                    </div>
                    <Switch
                      disabled={printerLocked}
                      checked={form.tablePrintBillRequest === true}
                      onCheckedChange={(v) => setForm((prev) => ({ ...prev, tablePrintBillRequest: v }))}
                      data-testid="settings-conta-pedida-imprime"
                    />
                  </div>
                  {form.tablePrintBillRequest === true && (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[
                        { valor: true, titulo: "A conta completa", texto: form.tableQrPerClient === true ? "Os pedidos da comanda e o total" : "Os pedidos da mesa e o total" },
                        { valor: false, titulo: "Só o aviso", texto: form.tableQrPerClient === true ? "O nome e o WhatsApp de quem pediu" : "«A mesa 6 pediu a conta»" },
                      ].map((o) => (
                        <button
                          key={String(o.valor)}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, tablePrintFullBill: o.valor }))}
                          aria-pressed={(form.tablePrintFullBill !== false) === o.valor}
                          className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                            (form.tablePrintFullBill !== false) === o.valor ? "border-primary bg-primary/10" : "bg-background hover:bg-muted"
                          }`}
                          data-testid={`settings-conta-pedida-${o.valor ? "completa" : "aviso"}`}
                        >
                          <span className="block text-sm font-medium">{o.titulo}</span>
                          <span className="block text-xs text-muted-foreground">{o.texto}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* «Imprimir a conta ao fechar» (Pedro, 01/11/2026, A-0065): o «Fechar conta» da página Comandas/Mesas. */}
              {!!form.tableOrdersEnabled && (
                <div className="flex items-center justify-between gap-3 border-t pt-3" data-testid="settings-conta-ao-fechar">
                  <div>
                    <p className="text-sm font-medium">Imprimir a conta ao fechar</p>
                    <p className="text-xs text-muted-foreground">
                      Ao tocar em «Fechar conta» em {form.tableQrPerClient === true ? "Comandas" : "Mesas"}, sai a conta completa, com o total e a forma de pagamento.
                    </p>
                  </div>
                  <Switch
                    disabled={printerLocked}
                    checked={form.tablePrintOnClose === true}
                    onCheckedChange={(v) => setForm((prev) => ({ ...prev, tablePrintOnClose: v }))}
                    data-testid="settings-conta-ao-fechar-imprime"
                  />
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Imprimir ao mudar status</p>
                    <p className="text-xs text-muted-foreground">Imprime ao atualizar o status de um pedido</p>
                  </div>
                  <Switch
                    disabled={printerLocked}
                    checked={autoPrintStatusChange}
                    onCheckedChange={v => {
                      if (v) {
                        setAutoPrintStatus(true, autoPrintStatusList.length ? autoPrintStatusList : ["confirmado"]);
                      } else {
                        setAutoPrintStatus(false, autoPrintStatusList);
                      }
                    }}
                  />
                </div>
                {autoPrintStatusChange && (
                  <div className="space-y-2 pl-1">
                    <p className="text-xs text-muted-foreground">Imprime ao atualizar o status de um pedido para:</p>
                    <select
                      className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background cursor-pointer"
                      value=""
                      onChange={e => {
                        const v = e.target.value;
                        if (!v) return;
                        if (autoPrintStatusList.includes(v)) return;
                        setAutoPrintStatus(true, [...autoPrintStatusList, v]);
                      }}
                    >
                      <option value="">Adicionar status…</option>
                      {ALL_ORDER_STATUSES_FOR_PRINT
                        .filter(s => !autoPrintStatusList.includes(s.id))
                        .map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <div className="flex flex-wrap gap-2">
                      {autoPrintStatusList.map(id => {
                        const label = ALL_ORDER_STATUSES_FOR_PRINT.find(s => s.id === id)?.label || id;
                        return (
                          <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/20">
                            {label}
                            <button
                              type="button"
                              className="hover:bg-primary/20 rounded-full p-0.5 cursor-pointer"
                              onClick={() => {
                                const next = autoPrintStatusList.filter(s => s !== id);
                                setAutoPrintStatus(next.length > 0, next);
                              }}
                              aria-label={`Remover ${label}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Quantidade de vias de impressão</p>
                  <p className="text-xs text-muted-foreground">Imprime o mesmo cupom N vezes (ex: 1 via cozinha + 1 motoboy)</p>
                </div>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={10}
                  disabled={printerLocked}
                  value={printCopiesText}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "");
                    setPrintCopiesText(raw);
                    if (raw !== "") {
                      const n = Math.min(Math.max(parseInt(raw, 10), 1), 10);
                      setForm((prev) => ({ ...prev, printCopies: n }));
                    }
                  }}
                  onBlur={() => {
                    const n = Math.min(Math.max(parseInt(printCopiesText || "1", 10) || 1, 1), 10);
                    setPrintCopiesText(String(n));
                    setForm((prev) => ({ ...prev, printCopies: n }));
                  }}
                  className="w-20 text-center shrink-0"
                />
              </div>
              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Espaço no topo da comanda</p>
                  <p className="text-xs text-muted-foreground">Linhas em branco antes do cupom, pra comanda presa na comandeira não cobrir o pedido</p>
                </div>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={8}
                  disabled={printerLocked}
                  value={form.receiptTopMargin ? form.receiptTopMargin : ""}
                  placeholder="0"
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "");
                    setForm((prev) => ({ ...prev, receiptTopMargin: raw === "" ? 0 : Math.min(parseInt(raw, 10) || 0, 8) }));
                  }}
                  className="w-20 text-center shrink-0"
                  data-testid="settings-receipt-top-margin"
                />
              </div>
              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Campo "Pedido Pago?" na comanda</p>
                  <p className="text-xs text-muted-foreground">Quadradinho pra marcar <span className="underline">de caneta</span> se o pedido já foi pago, pro motoboy não cobrar de novo.</p>
                </div>
                <Switch
                  disabled={printerLocked}
                  checked={!!form.receiptPaidCheckbox}
                  onCheckedChange={(v) => setForm((prev) => ({ ...prev, receiptPaidCheckbox: v }))}
                  data-testid="settings-receipt-paid-checkbox"
                />
              </div>
          </Card>
        </SectionErrorBoundary>
        </>
        );
      })()}
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-pdv" label="PDV (Ponto de Venda)">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Printer className="w-5 h-5 text-primary" />
              PDV (Ponto de Venda)
            </div>
            <p className="text-sm text-muted-foreground">
              Configure o comportamento padrão do balcão.
            </p>

            <div className="flex items-center justify-between gap-4 py-2 border-t border-border">
              <div>
                <Label className="text-sm font-medium">Imprimir cupom automaticamente ao finalizar</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Envia o pedido direto à impressora térmica.</p>
              </div>
              <Switch
                checked={!!(form as any).pdvSettings?.autoPrint}
                onCheckedChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), autoPrint: v } } as any)}
                data-testid="pdv-settings-autoprint"
              />
            </div>

            <div className="flex items-center justify-between gap-4 py-2 border-t border-border">
              <div>
                <Label className="text-sm font-medium">Cliente obrigatório</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Bloqueia finalizar sem identificar o cliente.</p>
              </div>
              <Switch
                checked={!!(form as any).pdvSettings?.customerRequired}
                onCheckedChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), customerRequired: v } } as any)}
                data-testid="pdv-settings-customer-required"
              />
            </div>

            <div className="space-y-1.5 py-2 border-t border-border">
              <Label className="text-sm font-medium">Status padrão dos pedidos do PDV</Label>
              <p className="text-xs text-muted-foreground">Pedido criado no balcão já entra com este status.</p>
              <select
                className="w-full md:w-64 border rounded-md p-2 text-sm bg-background"
                value={(form as any).pdvSettings?.defaultStatus || "confirmado"}
                onChange={(e) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), defaultStatus: e.target.value } } as any)}
                data-testid="pdv-settings-default-status"
              >
                <option value="confirmado">Confirmado</option>
                <option value="em_preparo">Em preparo</option>
                <option value="em_separacao">Em separação</option>
                <option value="pronto">Pronto</option>
                <option value="entregue">Entregue</option>
                <option value="novo">Novo (pendente)</option>
              </select>
            </div>

            <div className="space-y-3 py-2 border-t border-border">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium">Taxa de entrega do PDV é diferente</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Por padrão o PDV usa a mesma configuração de entrega do link público.
                    Ative para definir uma taxa fixa e pedido mínimo só para o balcão.
                  </p>
                </div>
                <Switch
                  checked={!!(form as any).pdvSettings?.useCustomDelivery}
                  onCheckedChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), useCustomDelivery: v } } as any)}
                  data-testid="pdv-settings-use-custom-delivery"
                />
              </div>
              {!!(form as any).pdvSettings?.useCustomDelivery && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Taxa de entrega do PDV</Label>
                    <CurrencyInput
                      value={(form as any).pdvSettings?.customDeliveryFee ?? "0"}
                      onChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), customDeliveryFee: v } } as any)}
                      placeholder="0,00"
                    />
                    <p className="text-xs text-muted-foreground">Cobrada quando o pedido for "entrega".</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Pedido mínimo do PDV</Label>
                    <CurrencyInput
                      value={(form as any).pdvSettings?.customMinOrderValue ?? ""}
                      onChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), customMinOrderValue: v } } as any)}
                      placeholder="Opcional"
                    />
                    <p className="text-xs text-muted-foreground">Deixe em branco para não exigir mínimo no balcão.</p>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-sm">Entrega grátis acima de (PDV)</Label>
                    <CurrencyInput
                      value={(form as any).pdvSettings?.customFreeDeliveryAbove ?? ""}
                      onChange={(v) => setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), customFreeDeliveryAbove: v } } as any)}
                      placeholder="Deixe vazio para não aplicar"
                    />
                    <p className="text-xs text-muted-foreground">Quando o subtotal do pedido no balcão atingir este valor, a taxa de entrega vira grátis.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Taxa de serviço (Pedro, 30/10/2026): só no «Fechar conta» do PDV, que desliga na conta de quem não paga; o link
                e a entrega nunca têm. A porcentagem vai como o lojista digitou («12,5»): o servidor confere e grava número. */}
            {(() => {
              const taxa = (form as any).pdvSettings?.taxaServico as { ativo?: boolean; percentual?: number | string; despesa?: boolean; aviso?: boolean } | undefined;
              const ativo = !!taxa?.ativo;
              const pct = taxa?.percentual ?? 10;
              const pctTexto = String(pct).replace(".", ",");
              const pctNum = Number(String(pct).replace(",", "."));
              const pctValido = Number.isFinite(pctNum) && pctNum > 0 && pctNum <= 100;
              const mudar = (t: { ativo?: boolean; percentual?: number | string; despesa?: boolean; aviso?: boolean }) =>
                setForm({ ...form, pdvSettings: { ...((form as any).pdvSettings || {}), taxaServico: { ativo, percentual: pct, ...(taxa?.despesa ? { despesa: true } : {}), ...(taxa?.aviso === false ? { aviso: false } : {}), ...t } } } as any);
              return (
                <div className="space-y-3 py-2 border-t border-border">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">Cobrar {pctValido ? pctTexto : "10"}% de taxa de serviço</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Entra no «Fechar conta» do PDV, que pode tirar em cada conta. Nunca no link nem na entrega.</p>
                    </div>
                    <Switch checked={ativo} onCheckedChange={(v) => mudar({ ativo: v })} data-testid="pdv-settings-taxa-servico" />
                  </div>
                  {ativo && (
                    <div className="space-y-1.5 pl-1">
                      <Label className="text-sm" htmlFor="pdv-taxa-servico-pct">Porcentagem</Label>
                      <div className="relative w-32">
                        <Input
                          id="pdv-taxa-servico-pct"
                          inputMode="decimal"
                          value={pctTexto}
                          onChange={(e) => mudar({ ativo: true, percentual: e.target.value.replace(/[^\d,.]/g, "").slice(0, 6) })}
                          className="pr-8"
                          data-testid="pdv-settings-taxa-servico-pct"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                      </div>
                      {!pctValido && <p className="text-xs text-destructive">De 0,01% a 100%.</p>}
                    </div>
                  )}
                  {/* «Lançar a taxa de serviço como despesa» (Profissional, Pedro, 30/10/2026), como a da entrega. Sem o recurso
                      só dá para desligar: a chave já ligada (plano que venceu) continua podendo sair. */}
                  {ativo && (
                    <div className="flex items-start justify-between gap-3 pt-3 border-t border-border" data-testid="pdv-settings-taxa-servico-despesa">
                      <div className="min-w-0">
                        <Label className="text-sm font-medium">
                          Lançar a taxa de serviço como despesa {!pagaPlano && <UltraBadge plan="profissional" className="ml-1 align-middle" />}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Entra no Financeiro quando a conta é fechada.</p>
                      </div>
                      <Switch
                        className="mt-0.5 shrink-0"
                        checked={taxa?.despesa === true}
                        disabled={!podeTaxaServicoDespesa && taxa?.despesa !== true}
                        onCheckedChange={(v) => mudar({ despesa: v })}
                        data-testid="pdv-settings-taxa-servico-despesa-switch"
                      />
                    </div>
                  )}
                  {/* «Avisar na conta que a taxa é opcional» (Pedro, 30/10/2026): ligado por padrão; desligado, a conta
                      impressa sai sem «A taxa de serviço é opcional.». */}
                  {ativo && (
                    <div className="flex items-start justify-between gap-3 pt-3 border-t border-border" data-testid="pdv-settings-taxa-servico-aviso">
                      <div className="min-w-0">
                        <Label className="text-sm font-medium">Avisar na conta que a taxa é opcional</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Sai embaixo do total: «A taxa de serviço é opcional.»</p>
                      </div>
                      <Switch
                        className="mt-0.5 shrink-0"
                        checked={taxa?.aviso !== false}
                        onCheckedChange={(v) => mudar({ aviso: v })}
                        data-testid="pdv-settings-taxa-servico-aviso-switch"
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </Card>
        </SectionErrorBoundary>
      )}
      {showProducts && (
        <>
          <hr className="border-border/40" />
          {/* Pedro, 29/10/2026: a mesa e a comanda individual em cartões separados (usam os mesmos QR codes: um de cada vez). */}
          {(["mesa", "comanda"] as const).map((modo) => (
            <SectionErrorBoundary key={modo} anchorId={`cfg-${modo}`} label={modo === "mesa" ? "Pedido na mesa" : "Comanda individual"}>
              <TableOrdersSection
                modo={modo}
                enabled={!!form.tableOrdersEnabled}
                count={form.tableCount ?? null}
                porCliente={form.tableQrPerClient === true}
                contaCompleta={form.tablePrintFullBill !== false}
                imprimeConta={form.tablePrintBillRequest === true}
                soPeloQr={form.tableQrOnly === true}
                mostrarFechadas={form.tableShowClosed !== false}
                podeUsar={podeFeature("table_orders")}
                mostrarSelo={mostrarSelo}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              />
            </SectionErrorBoundary>
          ))}
        </>
      )}
      <hr className="border-border/40" />
      {showProducts && (
        <SectionErrorBoundary anchorId="cfg-mensagens" label="Mensagens de Status do Pedido">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <MessageCircleIcon className="w-5 h-5 text-primary" />
              Mensagens de Status do Pedido
            </div>
            <p className="text-sm text-muted-foreground">
              Configure mensagens automáticas via WhatsApp para cada mudança de status.
              Quando ativada, um popup aparecerá ao alterar o status do pedido.
            </p>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              <p className="font-medium mb-1">Variáveis disponíveis:</p>
              <div className="flex flex-wrap gap-2">
                {["{cliente_nome}", "{numero_pedido}", "{itens}", "{total}", "{loja_nome}"].map(v => (
                  <code key={v} className="bg-background px-1.5 py-0.5 rounded text-xs border">{v}</code>
                ))}
              </div>
              <p className="font-medium mt-2 mb-1">Variáveis específicas para "Enviar frete":</p>
              <div className="flex flex-wrap gap-2">
                {["{frete}", "{total_produtos}", "{total_atualizado}"].map(v => (
                  <code key={v} className="bg-background px-1.5 py-0.5 rounded text-xs border">{v}</code>
                ))}
              </div>
              <p className="font-medium mt-2 mb-1">Troco, em "Enviar frete" e na mensagem do motoboy:</p>
              <div className="flex flex-wrap gap-2 items-center" data-testid="settings-variavel-troco">
                <code className="bg-background px-1.5 py-0.5 rounded text-xs border">{"{troco}"}</code>
                <span>vira "Troco para R$ 50,00: R$ 29,00" quando o cliente pede troco. Se a mensagem não tiver {"{troco}"}, a linha entra logo depois do total.</span>
              </div>
              {!!form.loyaltyEnabled && (
                <>
                  <p className="font-medium mt-2 mb-1">Para "Entregue", com o programa de pontos:</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <code className="bg-background px-1.5 py-0.5 rounded text-xs border">{"{pontos}"}</code>
                    <span>vira "Você ganhou 42 pontos. Saldo: 382 pontos."</span>
                  </div>
                </>
              )}
            </div>
            <div className="space-y-4">
              {Object.entries(STATUS_MESSAGE_DEFAULTS).filter(([id]) => id !== "avisou_motoboy").map(([statusId, defaults]) => {
                const templates = form.statusMessageTemplates || {};
                const entry = templates[statusId] || { template: defaults.template, enabled: true };
                const updateTemplate = (field: string, value: string | boolean) => {
                  const current = form.statusMessageTemplates || {};
                  const currentEntry = current[statusId] || { template: defaults.template, enabled: true };
                  setForm({
                    ...form,
                    statusMessageTemplates: {
                      ...current,
                      [statusId]: { ...currentEntry, [field]: value },
                    },
                  });
                };
                const isCustom = comoSai(statusId, entry.template) !== defaults.template;
                const restoreDefault = () => {
                  const current = { ...(form.statusMessageTemplates || {}) };
                  const currentEntry = current[statusId] || { template: defaults.template, enabled: true };
                  current[statusId] = { ...currentEntry, template: defaults.template };
                  setForm({ ...form, statusMessageTemplates: current });
                };
                return (
                  <div key={statusId} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">{defaults.label}</Label>
                      <div className="flex items-center gap-2">
                        {isCustom && (
                          <button
                            type="button"
                            onClick={restoreDefault}
                            className="text-xs text-muted-foreground hover:text-destructive"
                          >
                            Restaurar padrão
                          </button>
                        )}
                        <span className="text-xs text-muted-foreground">{entry.enabled ? "Popup ativo" : "Sem popup"}</span>
                        <Switch
                          checked={entry.enabled}
                          onCheckedChange={(v) => updateTemplate("enabled", v)}
                        />
                      </div>
                    </div>
                    <textarea
                      value={entry.template}
                      onChange={(e) => updateTemplate("template", e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
                    />
                  </div>
                );
              })}
            </div>

            {(() => {
              const motoboyDefaults = STATUS_MESSAGE_DEFAULTS["avisou_motoboy"];
              const templates = form.statusMessageTemplates || {};
              const entry = templates["avisou_motoboy"] || { template: motoboyDefaults.template, enabled: true };
              const updateTemplate = (field: string, value: string | boolean) => {
                const current = form.statusMessageTemplates || {};
                const currentEntry = current["avisou_motoboy"] || { template: motoboyDefaults.template, enabled: true };
                setForm({
                  ...form,
                  statusMessageTemplates: {
                    ...current,
                    avisou_motoboy: { ...currentEntry, [field]: value },
                  },
                });
              };
              return (
                <div className="mt-6 pt-6 border-t space-y-4">
                  <div className="flex items-center gap-2 text-base font-semibold">
                    🏍️ Mensagem para o Motoboy
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Mensagem enviada ao motoboy quando o status "Avisou Motoboy" é selecionado. Os dados do pedido são preenchidos automaticamente.
                  </p>
                  <div className="text-xs text-muted-foreground bg-sky-50 dark:bg-sky-950/20 rounded-lg p-3 border border-sky-200 dark:border-sky-800">
                    <p className="font-medium mb-1">Variáveis disponíveis:</p>
                    <div className="flex flex-wrap gap-2">
                      {["{motoboy_nome}", "{numero_pedido}", "{cliente_nome}", "{cliente_whatsapp}", "{endereco}", "{itens}", "{pagamento}", "{valor_cobrar}", "{troco}", "{taxa_entrega}", "{obs}"].map(v => (
                        <code key={v} className="bg-background px-1.5 py-0.5 rounded text-xs border">{v}</code>
                      ))}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 space-y-3 border-sky-200 dark:border-sky-800">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">{motoboyDefaults.label}</Label>
                      <div className="flex items-center gap-2">
                        {comoSai("avisou_motoboy", entry.template) !== motoboyDefaults.template && (
                          <button
                            type="button"
                            onClick={() => {
                              const current = { ...(form.statusMessageTemplates || {}) };
                              const currentEntry = current["avisou_motoboy"] || { template: motoboyDefaults.template, enabled: true };
                              current["avisou_motoboy"] = { ...currentEntry, template: motoboyDefaults.template };
                              setForm({ ...form, statusMessageTemplates: current });
                            }}
                            className="text-xs text-muted-foreground hover:text-destructive"
                          >
                            Restaurar padrão
                          </button>
                        )}
                        <span className="text-xs text-muted-foreground">{entry.enabled ? "Popup ativo" : "Sem popup"}</span>
                        <Switch
                          checked={entry.enabled}
                          onCheckedChange={(v) => updateTemplate("enabled", v)}
                        />
                      </div>
                    </div>
                    <textarea
                      value={entry.template}
                      onChange={(e) => updateTemplate("template", e.target.value)}
                      rows={10}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
                    />
                  </div>
                </div>
              );
            })()}
          </Card>
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      <SectionErrorBoundary anchorId="cfg-robo" label="Robô do WhatsApp">
        <WhatsAppBotSection podeUsar={podeFeature("whatsapp_bot")} mostrarSelo={mostrarSelo} showProducts={showProducts} />
      </SectionErrorBoundary>
      {showServices && (
        <SectionErrorBoundary label="Mensagem de Confirmação de Agendamento">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <MessageCircleIcon className="w-5 h-5 text-primary" />
              Mensagem de Confirmação de Agendamento
            </div>
            <p className="text-sm text-muted-foreground">
              Mensagem que <strong>você envia para o cliente</strong> quando confirma o agendamento no admin (status "Confirmado"). Os dados são preenchidos automaticamente.
            </p>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              <p className="font-medium mb-1">Variáveis disponíveis:</p>
              <div className="flex flex-wrap gap-2">
                {["{cliente_nome}", "{servico}", "{data}", "{hora}", "{duracao}", "{preco}", "{profissional}", "{loja_nome}"].map(v => (
                  <code key={v} className="bg-background px-1.5 py-0.5 rounded text-xs border">{v}</code>
                ))}
              </div>
            </div>
            {(() => {
              const defaultMsg = "Olá {cliente_nome}! Seu agendamento foi confirmado:\n\nServiço: {servico}\nData: {data}\nHorário: {hora}\nDuração: {duracao}\nValor: {preco}\n\nAté lá!";
              const current = form.appointmentMessageTemplate ?? defaultMsg;
              const isCustom = current !== defaultMsg;
              return (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Mensagem do agendamento</Label>
                {isCustom && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, appointmentMessageTemplate: null })}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Restaurar padrão
                  </button>
                )}
              </div>
              <textarea
                value={current}
                onChange={(e) => setForm({ ...form, appointmentMessageTemplate: e.target.value })}
                rows={8}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
              />
            </div>
              );
            })()}
          </Card>
        </SectionErrorBoundary>
      )}
      {showServices && (
        <SectionErrorBoundary label="Mensagem do Cliente ao Agendar">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <MessageCircleIcon className="w-5 h-5 text-primary" />
              Mensagem do Cliente ao Agendar
            </div>
            <p className="text-sm text-muted-foreground">
              Mensagem que o <strong>cliente envia para você</strong> via WhatsApp ao concluir o agendamento na agenda pública. Os dados são preenchidos automaticamente.
            </p>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              <p className="font-medium mb-1">Variáveis disponíveis:</p>
              <div className="flex flex-wrap gap-2">
                {["{cliente_nome}", "{servico}", "{data}", "{hora}", "{duracao}", "{preco}", "{profissional}", "{loja_nome}"].map(v => (
                  <code key={v} className="bg-background px-1.5 py-0.5 rounded text-xs border">{v}</code>
                ))}
              </div>
            </div>
            {(() => {
              const defaultMsg = "Olá! Acabei de agendar pelo link 📅\n\n*Cliente:* {cliente_nome}\n*Serviço:* {servico}\n*Data:* {data}\n*Horário:* {hora}\n*Duração:* {duracao}\n*Valor:* {preco}";
              const current = form.appointmentRequestTemplate ?? defaultMsg;
              const isCustom = current !== defaultMsg;
              return (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Mensagem do pedido de agendamento</Label>
                {isCustom && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, appointmentRequestTemplate: null })}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Restaurar padrão
                  </button>
                )}
              </div>
              <textarea
                value={current}
                onChange={(e) => setForm({ ...form, appointmentRequestTemplate: e.target.value })}
                rows={8}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]"
              />
            </div>
              );
            })()}
          </Card>
        </SectionErrorBoundary>
      )}
      <hr className="border-border/40" />
      <SectionErrorBoundary label="Links Públicos">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold"><LinkIcon className="w-5 h-5 text-primary" /> Links Públicos</div>
          {showProducts && (
            <div className="space-y-2">
              <Label className="text-primary">Link da Loja</Label>
              <div className="flex gap-2">
                <Input value={displayUrl(catalogUrl)} readOnly className="flex-1 min-w-0 border-primary/30" />
                <Button variant="outline" size="icon" className="shrink-0 cursor-pointer" onClick={() => copyLink(catalogUrl, "catalog")}>
                  {copiedLink === "catalog" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="icon" className="shrink-0 cursor-pointer" onClick={() => window.open(catalogUrl, "_blank")}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {showServices && (
            <div className="space-y-2">
              <Label className="text-primary">Link da Agenda</Label>
              <div className="flex gap-2">
                <Input value={displayUrl(agendaUrl)} readOnly className="flex-1 min-w-0 border-primary/30" />
                <Button variant="outline" size="icon" className="shrink-0 cursor-pointer" onClick={() => copyLink(agendaUrl, "agenda")}>
                  {copiedLink === "agenda" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="icon" className="shrink-0 cursor-pointer" onClick={() => window.open(agendaUrl, "_blank")}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </SectionErrorBoundary>
      <hr className="border-border/40" />
      {settings.plan === "gratis" && (
        <SectionErrorBoundary anchorId="cfg-assinatura" label="Plano">
          <PlanPicker
            used={settings.orgMonthlyOrderCount ?? settings.monthlyOrderCount ?? 0}
            freeOrderLimit={(settings as any).freeOrderLimit || 30}
            nextResetDate={(settings as any).nextOrderResetDate ? new Date((settings as any).nextOrderResetDate) : null}
            subscribing={subscribing}
            onSubscribe={async (plan, cycle) => {
              const valueReais = priceCents(plan, cycle) / 100;
              trackEvent("InitiateCheckout", user?.user?.id, valueReais);
              setLocation(`/assinar?cycle=${cycle}&plan=${plan}`);
            }}
            showProducts={showProducts}
            showServices={showServices}
            printerConnected={printer.connected}
          />
          <SubscriptionCharges standalone />
        </SectionErrorBoundary>
      )}
      {settings.plan !== "gratis" && (() => {
        const planId: PaidPlanId = isPaidPlanId(settings.plan) ? settings.plan : "profissional";
        const cycle = (settings.planBillingCycle === "yearly" ? "yearly" : "monthly") as BillingCycleUI;
        const planName = PLAN_DEFS[planId].name;
        const cycleLabel = cycle === "yearly" ? "anual" : "mensal";
        const units = settings.orgUnitCount ?? 1;
        const total = totalCents(planId, cycle, units);
        const features = PLAN_DEFS[planId].features;

        return (
          <SectionErrorBoundary anchorId="cfg-assinatura" label="Gerenciar Assinatura">
            <Card className="p-6 space-y-3">
              <button
                type="button"
                className="flex items-center justify-between w-full cursor-pointer"
                onClick={() => setShowManageSub((v) => !v)}
              >
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Crown className="w-5 h-5 text-primary" /> Gerenciar Assinatura
                </div>
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showManageSub ? "rotate-180" : ""}`} />
              </button>

              {showManageSub && (
                <>
                  {settings.subscriptionAutoRenewBroken ? (
                  // Mandato PIX morto: a renovação automática VAI falhar. Estado
                  // honesto + CTA pro fluxo de reassinar (gera mandato novo) —
                  // "Reativar Assinatura" (unschedule-cancel) não resolve isso.
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl dark:bg-amber-950/30 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-lg flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <Crown className="w-5 h-5" />
                          Plano {planName} ({cycleLabel})
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          Renovação automática desativada
                          {settings.subscriptionCurrentPeriodEnd && (
                            <> — seu plano vale até {formatBRDate(settings.subscriptionCurrentPeriodEnd)}</>
                          )}
                        </p>
                      </div>
                      <span className="text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-1 rounded-full">Ação necessária</span>
                    </div>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      A autorização do PIX automático foi cancelada no seu banco. Reative a assinatura pra não perder o plano Profissional.
                    </p>
                    <Button
                      className="mt-1 cursor-pointer"
                      onClick={() => setLocation("/assinar")}
                    >
                      Reativar assinatura
                    </Button>
                  </div>
                ) : settings.subscriptionCancelAtPeriodEnd ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl dark:bg-amber-950/30 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-lg flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <Crown className="w-5 h-5" />
                          Plano {planName} ({cycleLabel})
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          Cancelamento agendado
                          {(settings.subscriptionCancelAt || settings.subscriptionCurrentPeriodEnd) && (
                            <> — acesso até {formatBRDate(settings.subscriptionCancelAt || settings.subscriptionCurrentPeriodEnd)}</>
                          )}
                        </p>
                      </div>
                      <span className="text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-1 rounded-full">Cancelando</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground mt-1 mb-2">
                      {features.map((f) => (
                        <span key={f} className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-amber-500" />
                          {f}
                          {f === "WhatsApp automático" && (
                            <span className="ml-1 text-[9px] uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-semibold">Em breve</span>
                          )}
                        </span>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      className="mt-2 cursor-pointer"
                      disabled={cancelLoading}
                      onClick={async () => {
                        setCancelLoading(true);
                        try {
                          await customFetch("/api/asaas/subscriptions/unschedule-cancel", {
                            method: "POST",
                          });
                          toast({ title: "Assinatura reativada", description: "O cancelamento agendado foi removido." });
                          queryClient.setQueryData<StoreSettings>(["settings"], (prev) =>
                            prev ? { ...prev, subscriptionCancelAtPeriodEnd: false, subscriptionCancelAt: null } : prev,
                          );
                          await queryClient.refetchQueries({ queryKey: ["settings"] });
                        } catch (err) {
                          toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" });
                        } finally {
                          setCancelLoading(false);
                        }
                      }}
                    >
                      {cancelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Reativar Assinatura
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl dark:bg-green-950/30 dark:border-green-800">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-lg flex items-center gap-2 text-green-700 dark:text-green-300">
                          <Crown className="w-5 h-5" />
                          Plano {planName} ({cycleLabel})
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {formatBRL(total)}/{cycle === "yearly" ? "ano" : "mês"}
                          {units > 1 && <> — {units} unidades</>}
                          {settings.subscriptionCurrentPeriodEnd && (() => {
                            // A data vem do vencimento da cobrança em aberto (ou do
                            // next_due). Cobrança que venceu e não foi paga deixa essa
                            // data no PASSADO até o rebaixamento em D+2 — aí "Renova em"
                            // viraria promessa com data vencida. Vencida = dia BRT
                            // anterior a hoje: no próprio dia do vencimento o débito
                            // ainda vai rodar, então segue "Renova em".
                            const dia = new Date(settings.subscriptionCurrentPeriodEnd).toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
                            const hoje = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
                            return (
                              <> — {dia < hoje ? "Venceu em" : "Renova em"} {formatBRDate(settings.subscriptionCurrentPeriodEnd)}</>
                            );
                          })()}
                        </p>
                      </div>
                      <span className="text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 px-2 py-1 rounded-full">Ativo</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground mt-1 mb-2">
                      {features.map((f) => (
                        <span key={f} className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-green-500" />
                          {f}
                          {f === "WhatsApp automático" && (
                            <span className="ml-1 text-[9px] uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-semibold">Em breve</span>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {planId !== "ultra" && (
                        <Button
                          className="cursor-pointer"
                          onClick={() => setLocation("/assinar?plan=ultra")}
                          data-testid="settings-upgrade-ultra"
                        >
                          <Rocket className="w-4 h-4 mr-1" /> Fazer upgrade para o Ultra
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="cursor-pointer"
                        onClick={() => setLocation("/assinar")}
                      >
                        Gerenciar Assinatura
                      </Button>
                      <Button
                        variant="ghost"
                        className="cursor-pointer text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setShowCancelDialog(true)}
                      >
                        Cancelar Assinatura
                      </Button>
                    </div>
                  </div>
                  )}
                  <SubscriptionCharges />
                </>
              )}
            </Card>
          </SectionErrorBoundary>
        );
      })()}
      <AlertDialog open={avisoDePausa} onOpenChange={setAvisoDePausa}>
        {/* No celular, a margem dos outros pop-ups do painel (a do ModalShell) e os cantos arredondados (Pedro, 30/10/2026:
            «encosta nas bordas e fica sem os cantos arredondados»). */}
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-lg" data-testid="aviso-loja-pausada">
          <AlertDialogHeader className="items-center text-center sm:text-center">
            <PauseCircle className="w-12 h-12 text-amber-600" />
            <AlertDialogTitle>Loja pausada</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-center">
              O seu cardápio não recebe pedidos até você desligar este botão.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center">
            <AlertDialogAction className="w-full sm:w-auto sm:min-w-32" data-testid="aviso-loja-pausada-ok">Entendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar assinatura?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  A sua assinatura será cancelada automaticamente em{" "}
                  <strong>
                    {formatBRDate(settings.subscriptionCurrentPeriodEnd)}
                  </strong>
                  .
                </p>
                <p>Até lá, você continua com acesso total ao plano.</p>
                <p>Não haverá nova cobrança.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelLoading}
            >
              Voltar
            </Button>
            <Button
              variant="destructive"
              disabled={cancelLoading}
              onClick={async () => {
                setCancelLoading(true);
                try {
                  await customFetch("/api/asaas/subscriptions/schedule-cancel", {
                    method: "POST",
                  });
                  toast({
                    title: "Cancelamento agendado",
                    description: "Sua assinatura será encerrada na data da próxima cobrança.",
                  });
                  setShowCancelDialog(false);
                  queryClient.setQueryData<StoreSettings>(["settings"], (prev) =>
                    prev
                      ? {
                          ...prev,
                          subscriptionCancelAtPeriodEnd: true,
                          subscriptionCancelAt: prev.subscriptionCurrentPeriodEnd ?? null,
                        }
                      : prev,
                  );
                  await queryClient.refetchQueries({ queryKey: ["settings"] });
                } catch (err) {
                  toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" });
                } finally {
                  setCancelLoading(false);
                }
              }}
            >
              {cancelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Agendar Cancelamento
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card className="p-6">
        <Button
          variant="ghost"
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 cursor-pointer"
          onClick={() => user && logout()}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sair da conta
        </Button>
      </Card>
      <AlertDialog open={showQueueDialog} onOpenChange={(open) => { if (!queueDialogBusy) setShowQueueDialog(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fila de impressão pendente</AlertDialogTitle>
            <AlertDialogDescription>
              Existem {printer.queue.length} {printer.queue.length === 1 ? "pedido" : "pedidos"} aguardando impressão. O que você quer fazer ao conectar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="cursor-pointer"
              disabled={queueDialogBusy}
              onClick={() => setShowQueueDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              className="cursor-pointer text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
              disabled={queueDialogBusy}
              onClick={async () => {
                setQueueDialogBusy(true);
                try {
                  await printer.clearQueue();
                  const result = await printer.connect();
                  setShowQueueDialog(false);
                  if (result.ok) toast({ title: "Impressora conectada!", description: "Fila excluída." });
                  else toast({ title: "Não foi possível conectar", description: result.error, variant: "destructive" });
                } finally {
                  setQueueDialogBusy(false);
                }
              }}
            >
              {queueDialogBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Excluir fila
            </Button>
            <Button
              className="cursor-pointer"
              disabled={queueDialogBusy}
              onClick={async () => {
                setQueueDialogBusy(true);
                try {
                  const result = await printer.connect();
                  setShowQueueDialog(false);
                  if (result.ok) toast({ title: "Impressora conectada!", description: "Imprimindo a fila..." });
                  else toast({ title: "Não foi possível conectar", description: result.error, variant: "destructive" });
                } finally {
                  setQueueDialogBusy(false);
                }
              }}
            >
              {queueDialogBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
              Imprimir fila
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showPrintAgentSuggestion} onOpenChange={setShowPrintAgentSuggestion}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Não foi possível conectar a impressora pelo navegador</AlertDialogTitle>
            <AlertDialogDescription>
              Esta impressora precisa do Aplicativo de Impressão para Windows. Depois de instalar, cole nele o token de conexão.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="cursor-pointer">Fechar</AlertDialogCancel>
            {/* Leva pro token em vez de explicar onde ele fica: a seção já traz
                o código com o rótulo "cole no app Windows ou Android". */}
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={() => {
                setShowPrintAgentSuggestion(false);
                setTimeout(() => {
                  document.getElementById("cfg-impressora")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 150);
              }}
            >
              Ver token
            </Button>
            <Button asChild className="cursor-pointer">
              <a
                href="/download/print-agent"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowPrintAgentSuggestion(false)}
              >
                <Download className="w-4 h-4 mr-1" />
                Baixar aplicativo
              </a>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div id="cfg-fim" className="scroll-mt-28 md:scroll-mt-16" />
    </div>
  );
}

// Curadoria do "Mais vendidos": vazio = ranking real dos últimos 30 dias;
// com itens, o lojista manda (ordem = ordem do clique, mostrada no selo).
function FeaturedProductsPicker({ value, onChange }: { value: string[] | null; onChange: (ids: string[] | null) => void }) {
  const { data: prods = [] } = useListProducts();
  const escolhidos = value ?? [];
  const manual = escolhidos.length > 0;
  const toggle = (id: string) => {
    const next = escolhidos.includes(id) ? escolhidos.filter((x) => x !== id) : [...escolhidos, id].slice(0, 12);
    onChange(next.length > 0 ? next : null);
  };
  return (
    <div className="border-t pt-4 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" size="sm" variant={!manual ? "default" : "outline"} onClick={() => onChange(null)}>
          Automático
        </Button>
        <Button type="button" size="sm" variant={manual ? "default" : "outline"} onClick={() => { if (!manual && (prods as any[]).length > 0) toggle((prods as any[])[0].id); }}>
          Escolher produtos
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {manual ? "Toque pra incluir/tirar — o número é a posição na vitrine." : "Ranking real das vendas dos últimos 30 dias."}
      </p>
      {manual && (
        <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
          {(prods as any[]).map((p2) => {
            const pos = escolhidos.indexOf(p2.id);
            return (
              <button
                key={p2.id}
                type="button"
                onClick={() => toggle(p2.id)}
                className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm ${pos >= 0 ? "border-primary bg-primary/5" : "border-border/60"}`}
              >
                <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${pos >= 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {pos >= 0 ? pos + 1 : "+"}
                </span>
                <span className="truncate">{p2.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

